// Step 2 — Pick products + media.
//
// Two horizontal ribbons (products, media). Operator can pick from
// either; the picker fetches "related" items from the other side
// and surfaces them in a sub-ribbon below the main one. This mirrors
// the seeder logic on the backend: a media seeds creatives via its
// matched products; a product seeds creatives via its matched media.
//
// Pre-fill sources:
//   - Wizard URL params (?productIds, ?mediaIds) — already absorbed
//     into wizard state
//   - Campaign pinned items — when a campaign is chosen in Step 1, the
//     campaign's pinnedProducts / pinnedMedia auto-select here
//
// The render-cap (MAX_CREATIVES_PER_RUN = 6 on the backend) is mirrored
// in the helper text so operators understand why selecting many items
// won't proportionally produce more ads.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { Box, VStack, HStack, Text, Button, Wrap, WrapItem, Image, Tooltip, Badge, Input, Progress } from '@chakra-ui/react';
import type { WizardSelections, AdKindsSelection, SelectionSummary, SeedPick } from './index';
import { StepShell } from './index';
import { apiJson } from '../../auth/apiFetch';
import { useBrand } from '../../brand/BrandContext';
import { RibbonPicker, ProductTile, MediaTile } from './RibbonPicker';
import type { SeedUsage } from './RibbonPicker';
import { imageVideoKinds, useFormatCatalog } from './formatCatalog';
import type { FormatEntry } from './formatCatalog';

// Cloudinary video poster thumb — mirrors RibbonPicker.MediaTile so the
// Step 4 review strip shows a still frame rather than a broken video URL.
function mediaThumbUrl(m: { fileType: 'image' | 'video'; fileUrl: string }): string {
  if (m.fileType === 'video' && m.fileUrl.includes('/video/upload/')) {
    return m.fileUrl
      .replace('/video/upload/', '/video/upload/so_0/')
      .replace(/\.(mp4|mov|webm|m4v)(\?.*)?$/i, '.jpg$2');
  }
  return m.fileUrl;
}

type SeedUsageResp = { seeds: ({ seedKind: 'product' | 'media'; seedId: string } & SeedUsage)[] };

// Intersect the operator's adKinds choice with the format's declared
// kinds from GET /api/ads/formats (shared module-level cache with the
// shell picker). Reels is video-only — its catalog images are seeds only.
function effectiveAdKinds(
  entry: FormatEntry | undefined,
  adKinds: AdKindsSelection
): Array<'image' | 'video'> {
  const allowed = imageVideoKinds(entry);
  return allowed.filter(k => adKinds === 'both' || adKinds === k);
}

// Shared horizontal-ribbon scrollbar — used by per-product seed/queue rows
// so long product lists stay page-vertical while each row scrolls on X.
const RIBBON_SCROLL_SX = {
  '&::-webkit-scrollbar':       { height: '8px' },
  '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.18)', borderRadius: '4px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' }
} as const;

// How many catalog images the IMAGE-ad queue pre-picks per product.
//
// ONE — the catalog's FIRST image only. Owner rule (2026-08-03): catalog static
// ads seed from the first image that came from the catalog, not from whatever
// carries the imageRole 'hero' label, and never from social/UGC. In practice the
// catalog's first image IS the 'hero' row here, because catalogImagery is built
// from CatalogProduct.imageUrl followed by additionalImages[] — but the pre-pick
// below only fires when that row is actually materialised, because ids[] holds
// materialised rows only. This is NOT the video reference
// count and must never be derived from it: the scaffold's
// defaultReferenceCount (3) is the VIDEO stack size (3 distinct views +
// primary repeated as the closing beat), and reusing it here pre-picked three
// mediaIds for static too. Those picks POST as `mediaIds`, which the backend
// reads as a deliberate operator override — `universeTopN =
// Math.max(mediaIds.length, DIRECTOR_UNIVERSE_TOP_N)` — so a hero-only default
// (DIRECTOR_UNIVERSE_TOP_N=1) silently became a 3-image universe and the
// static render sent refs=3. Most visible on a kinds='both' run, where the
// operator sees the video rail's 3 and the image queue's 3 side by side.
// ...which is why this is now SERVED, not hardcoded: IMAGE_DEFAULT_REFERENCE_COUNT
// in the backend's config/defaults.env. The constant below is only the fallback
// used when the scaffold fetch fails, and it holds the historical value so a
// failed fetch is a no-op rather than a behaviour change. The independence the
// comment above insists on is now structural — video and static read two
// different env vars.
const IMAGE_QUEUE_DEFAULT_COUNT_FALLBACK = 1;

// Default-stack policy resolved by the backend from env. See
// services/referenceDefaultsService.js. shotTypes is an ORDERED PREFERENCE, not
// a filter: matching images sort forward, everything else keeps feed order
// behind them, and nothing is ever dropped.
type ReferenceDefaultsPolicy = {
  count:     number;
  shotTypes: string[];
};

// Scaffold response — maxReferenceImages / defaultReferenceCount ride
// alongside the existing prompt/model fields (backend buildPromptScaffold).
type RefLimitsScaffold = {
  model?:                 string;
  maxReferenceImages?:    number;
  defaultReferenceCount?: number;
  referenceDefaults?: {
    video?: { shotTypes?: string[] };
    image?: ReferenceDefaultsPolicy;
  };
};

// Stable preference ordering, mirroring referenceDefaultsService
// .orderByShotTypePreference on the backend. Same contract, and it must stay the
// same or the picker's pre-pick and the backend's own assembly disagree:
//   • returns the SAME members — a sort, never a filter
//   • empty preference list is a strict no-op (pure feed order)
//   • unmatched, including every null shotType, keeps relative feed order behind
//     the matched ones
function orderByShotTypePreference(rows: CatalogImageRow[], shotTypes: string[]): CatalogImageRow[] {
  if (rows.length < 2 || !shotTypes.length) return rows.slice();
  const bucketOf = (r: CatalogImageRow) => {
    if (!r.shotType) return shotTypes.length;
    const i = shotTypes.indexOf(String(r.shotType).toLowerCase());
    return i === -1 ? shotTypes.length : i;
  };
  return rows
    .map((r, i) => ({ r, i, b: bucketOf(r) }))
    .sort((a, b) => (a.b !== b.b ? a.b - b.b : a.i - b.i))
    .map(x => x.r);
}

// Match-evidence fields the picker surfaces on related-ribbon tiles.
// Backed by /api/catalog/:id/matches, /api/media/:id/related-products,
// and /api/brand/:id/brand-matches — all three return matchTier +
// outcomeReasoning so the tooltip shows "why this match" alongside
// the score and provider winner.
type MatchTier = 'product_match' | 'product_category' | 'brand_match';
type MatchEvidence = {
  matchTier:        MatchTier | null;
  outcomeReasoning: string | null;
  winner:           string | null;
  matchSource:      string | null;
  catalogCombinedScore: number | null;
};

type ProductRow = {
  id:        string;
  title:     string;
  imageUrl:  string | null;
  price?:    number | null;
  currency?: string | null;
  brand?:    string | null;
  source?:        string | null;     // CatalogProduct.source — 'ig-catalog' | 'manual-upload' | 'detect-identified'
  lastSyncedAt?:  string | null;     // ISO
  // Catalog-side imagery refs. URLs are the raw source-CDN strings;
  // *MediaId fields point at the wrapped catalog-product Media docs.
  // Used by the brand-kind unified ribbon to render alt tiles and
  // wire (productId, altMediaId) exclusions.
  additionalImages?:        string[];
  imageMediaId?:            string | null;
  additionalImageMediaIds?: string[];
  // 2026-08-18 — computed server-side (routes/catalog.js →
  // services/catalogImageQuality.js) from the raw imageUrl. True when the
  // product has no usable generation seed at all — either no image was
  // ever synced, or the only image on file is a broken thumbnail (Google
  // Shopping/Lens). Independent of imageMediaId, which can legitimately be
  // null while a real image is still pending detect materialization.
  seedUnusable?: boolean;
  seedIssue?:    'missing' | 'thumbnail-only' | null;
  // 2026-08-19 — extends the same vocabulary (not a parallel one): true
  // only once the row is BOTH seed-safe and materialized (imageMediaId
  // set). pickerBlockReason names why not, when it isn't — 'materializing'
  // is new; the other two values mirror seedIssue. See
  // catalogImageQuality.js's catalogSeedFields().
  pickerReady?:       boolean;
  pickerBlockReason?: 'missing' | 'thumbnail-only' | 'materializing' | null;
} & Partial<MatchEvidence> & {
  // Set on tiles surfaced from /api/media/:id/related-products. Used
  // as the "seed" when constructing the exclusion key for an X click.
  seedMediaId?: string;
};

type MediaRow = {
  id:                  string;
  fileType:            'image' | 'video';
  fileUrl:             string;
  creatorHandle:       string | null;
  primarySubjectLabel: string | null;
  postedAt?:           string | null;   // ISO; populated for IG posts surfaced via /api/catalog/:id/matches and /api/brand/:id/brand-matches
  source?:             string | null;   // Media.source — e.g. 'instagram', 'catalog-product'
} & Partial<MatchEvidence> & {
  seedProductId?: string | null;   // null for brand_match (no SKU)
  likes?:        number | null;
  comments?:     number | null;
  saves?:        number | null;
  engagement?:   number | null;   // 0–1 engagement rate (likes+comments / reach)
  postType?:     string | null;   // 'image' | 'video' | 'reel' | 'carousel' from IG/TikTok metadata
  shotType?:     string | null;   // 'lifestyle' | 'on_model' | 'product_only' | 'flat_lay' | etc.
  contentNature?:string | null;   // 'evergreen' | 'promotional' | 'announcement' | etc.
  adReadiness?:  number | null;   // 0–1 from adSuitability.score
};

// Pairing-key encoder. brand_match seeds use 'null' as the productId
// half so brand-wide exclusions don't collide with SKU-targeted ones.
function pairingKey(productId: string | null, mediaId: string): string {
  return `${productId || 'null'}|${mediaId}`;
}

// Catalog-imagery row shape — hero + alt URLs for a CatalogProduct.
// Shared between the picker and the brand-unified view, so declared
// at module scope rather than inside the component.
//
// imageMediaId is the catalog-product Media doc ID (when materialized
// by catalogProductDetectService); used to key (productId, altMediaId)
// exclusions so the operator can opt an alt out of the cartesian
// without dropping its product.
type CatalogImageRow = {
  productId:           string;
  productTitle:        string | null;
  productSource:       string | null;     // CatalogProduct.source — 'ig-catalog' | 'manual-upload' | 'detect-identified'
  productLastSyncedAt: string | null;     // ISO
  imageUrl:            string;
  imageMediaId:        string | null;
  role:                'hero' | 'alt';
  altIndex?:           number;
  // Media.classification.shotType, or null when unmaterialised or not yet
  // classified. Null is the NORMAL state — per-product detect is deferred
  // (CATALOG_DETECT_PRECOMPUTE=false), so a freshly ingested product has no
  // shot types at all. Used only to apply the configured shot-type PREFERENCE;
  // null must never exclude an image, or the newest products would default to
  // an empty stack.
  shotType?:           string | null;
};

// The product ribbon's empty state must tell two different situations
// apart: the FILTERED list came back empty (a search term matched
// nothing — the catalog itself is fine) vs the SOURCE list is empty (no
// catalog connected yet). Reusing one "connect a catalog" message for
// both tells an operator with an 800+ SKU catalog and a bad search term
// that their integration is broken. `catalogSize` is the last known
// unfiltered total (null until the first query-less fetch resolves).
function productRibbonEmptyHint(query: string, catalogSize: number | null): string {
  if (!query) return "No products in this brand's catalog yet. Connect a catalog to get started.";
  return catalogSize != null
    ? `No products match "${query}" — clear the search to see all ${catalogSize} product${catalogSize === 1 ? '' : 's'}.`
    : `No products match "${query}" — try clearing the search.`;
}

type Props = {
  value:    WizardSelections;
  onChange: (patch: Partial<WizardSelections>) => void;
  /**
   * True when the wizard was opened from a specific product's "Generate Ads"
   * button (?productIds=… on the entry URL). The operator named the subject of
   * this batch, so the campaign's pinned products/media must not be folded in
   * on top of it — see the pinned-hydration effect below.
   */
  deepLinkedToProduct?: boolean;
};

export function Step2Picker({ value, onChange, deepLinkedToProduct = false }: Props) {
  const { activeBrand } = useBrand();
  const brandId = activeBrand?.id || null;
  // Shared with PlatformFormatPicker via module-level cache — one request.
  const formatCatalog = useFormatCatalog();
  const selectedFormatEntry = formatCatalog.byKey.get(value.platformFormat);
  // Scene reel: the ordered seed rail becomes "preferred scene images" — its
  // own pick list (value.sceneSeedPicks, never pre-filled), at most
  // scenePickCap per product, catalog images only (the reel's source pool is
  // the product's catalog imagery, so a social post could never be used).
  const sceneReelMode = value.workflow === 'scene_reel';
  const scenePickCap = formatCatalog.sceneReelWorkflow?.maxSeedPicksPerProduct ?? 3;

  const [products, setProducts]         = useState<ProductRow[]>([]);
  const [media, setMedia]               = useState<MediaRow[]>([]);
  const [loadingP, setLoadingP]         = useState(false);
  const [loadingM, setLoadingM]         = useState(false);
  const [relatedMedia, setRelatedMedia] = useState<MediaRow[]>([]);
  const [seedUsage, setSeedUsage]       = useState<Map<string, SeedUsage>>(new Map());
  const [relatedProducts, setRelatedProducts] = useState<ProductRow[]>([]);

  // ── Search + pagination over the product catalog ──────────────────
  // Was previously a hard `limit=50` with no way to reach the rest of a
  // large catalog (a 214-SKU brand only ever showed the first 50 and had
  // no search box). `/api/catalog` already supports `offset`/`q` server-
  // side (routes/catalog.js) — this just wires the picker up to them.
  // PRODUCT_PAGE_SIZE matches the backend's hard max (`Math.min(…, 100)`
  // in routes/catalog.js) so one page is as large as a single request can be.
  const PRODUCT_PAGE_SIZE = 100;
  const [productSearchInput, setProductSearchInput] = useState('');   // live textbox value
  const [productQuery, setProductQuery]     = useState('');           // debounced value actually fetched
  const [productTotal, setProductTotal]     = useState(0);
  // Baseline unfiltered catalog size — captured only from a query-less
  // fetch (mount, or search cleared back to '') and never overwritten by
  // a filtered fetch. Lets the empty state below tell "search matched
  // nothing" apart from "catalog is actually empty" instead of reusing
  // productTotal, which goes to 0 for BOTH cases once a search is active.
  const [catalogSize, setCatalogSize]       = useState<number | null>(null);
  const [productHasMore, setProductHasMore] = useState(false);
  const [loadingMoreP, setLoadingMoreP]     = useState(false);
  // Bumped by PrepareCatalogBanner once a materialize drain finishes, so
  // the effect below re-fetches page 1 and picks up the freshly-stamped
  // imageMediaId/pickerReady values without a full component remount.
  const [catalogRefetchNonce, setCatalogRefetchNonce] = useState(0);

  // Debounce the textbox → productQuery so every keystroke doesn't fire
  // a request.
  useEffect(() => {
    const t = window.setTimeout(() => setProductQuery(productSearchInput.trim()), 300);
    return () => window.clearTimeout(t);
  }, [productSearchInput]);

  // Load brand catalog + media list. First page only here; loadMoreProducts
  // below pages in further results, and re-runs from offset 0 whenever the
  // search query changes.
  useEffect(() => {
    if (!brandId) return;
    let cancelled = false;
    setLoadingP(true);
    // Show every SKU variant as its own pickable tile in the product
    // ribbon. Sibling variants ALSO surface in the matched-media
    // ribbon when any one of them is picked (via the variants[] field
    // on /api/catalog/:id), where they can be deselected independently
    // via the existing exclude-pairing mechanism.
    const productQs = new URLSearchParams({ brandId, limit: String(PRODUCT_PAGE_SIZE) });
    if (productQuery) productQs.set('q', productQuery);
    apiJson<{ products: ProductRow[]; total?: number; hasMore?: boolean }>(`/api/catalog?${productQs.toString()}`)
      .then(res => {
        if (cancelled) return;
        const rows = res.products || [];
        setProducts(rows);
        const total = typeof res.total === 'number' ? res.total : rows.length;
        setProductTotal(total);
        if (!productQuery) setCatalogSize(total);
        setProductHasMore(!!res.hasMore);
      })
      .finally(() => { if (!cancelled) setLoadingP(false); });
    setLoadingM(true);
    apiJson<{ media: MediaRow[] }>(`/api/media?brandId=${encodeURIComponent(brandId)}&limit=50`)
      .then(res => {
        if (cancelled) return;
        // The list endpoint exposes mediaId not id — normalize.
        const rows = (res.media || []).map(m => ({
          ...(m as MediaRow & { mediaId?: string }),
          id: (m as MediaRow & { mediaId?: string }).mediaId || (m as MediaRow).id
        }));
        setMedia(rows);
      })
      .finally(() => { if (!cancelled) setLoadingM(false); });
    return () => { cancelled = true; };
  }, [brandId, productQuery, catalogRefetchNonce]);

  // Page in the next PRODUCT_PAGE_SIZE products (same `q`, offset advanced
  // past what's already loaded). Appends rather than replaces so scroll
  // position / selections on already-rendered tiles are undisturbed.
  const loadMoreProducts = () => {
    if (!brandId || loadingMoreP || !productHasMore) return;
    setLoadingMoreP(true);
    const qs = new URLSearchParams({
      brandId,
      limit:  String(PRODUCT_PAGE_SIZE),
      offset: String(products.length)
    });
    if (productQuery) qs.set('q', productQuery);
    apiJson<{ products: ProductRow[]; total?: number; hasMore?: boolean }>(`/api/catalog?${qs.toString()}`)
      .then(res => {
        const rows = res.products || [];
        setProducts(prev => {
          const have = new Set(prev.map(p => p.id));
          return [...prev, ...rows.filter(p => !have.has(p.id))];
        });
        if (typeof res.total === 'number') setProductTotal(res.total);
        setProductHasMore(!!res.hasMore);
      })
      .finally(() => setLoadingMoreP(false));
  };

  // Hydrate any pre-selected ids that aren't in the first page. The
  // wizard arrives with productIds/mediaIds from URL params (Generate
  // Ads triggered from the Media Library or Catalog Browser) or from
  // campaign pinned items — those ids aren't guaranteed to be in the
  // first PRODUCT_PAGE_SIZE the picker loads by default. Use the ?ids=
  // batch hydration we just added on the backend to fetch them, then
  // PREPEND to the ribbon arrays so they're highlighted at the front.
  useEffect(() => {
    if (!brandId) return;
    const known = new Set(products.map(p => p.id));
    const missing = value.productIds.filter(id => !known.has(id));
    if (!missing.length) return;
    let cancelled = false;
    apiJson<{ products: ProductRow[] }>(`/api/catalog?brandId=${encodeURIComponent(brandId)}&ids=${missing.join(',')}`)
      .then(res => {
        if (cancelled || !res.products?.length) return;
        setProducts(prev => {
          const have = new Set(prev.map(p => p.id));
          return [...res.products.filter(p => !have.has(p.id)), ...prev];
        });
      })
      .catch(() => { /* best-effort hydration */ });
    return () => { cancelled = true; };
  }, [brandId, value.productIds, products]);

  useEffect(() => {
    if (!brandId) return;
    const known = new Set(media.map(m => m.id));
    const missing = value.mediaIds.filter(id => !known.has(id));
    if (!missing.length) return;
    let cancelled = false;
    apiJson<{ media: MediaRow[] }>(`/api/media?brandId=${encodeURIComponent(brandId)}&ids=${missing.join(',')}`)
      .then(res => {
        if (cancelled || !res.media?.length) return;
        const rows = (res.media || []).map(m => ({
          ...(m as MediaRow & { mediaId?: string }),
          id: (m as MediaRow & { mediaId?: string }).mediaId || (m as MediaRow).id
        }));
        setMedia(prev => {
          const have = new Set(prev.map(m => m.id));
          return [...rows.filter(m => !have.has(m.id)), ...prev];
        });
      })
      .catch(() => { /* best-effort hydration */ });
    return () => { cancelled = true; };
  }, [brandId, value.mediaIds, media]);

  // Campaign display name for Step 4's review summary. Local so the
  // summary-lift effect below can compose it with product/media rows
  // without racing the pinned-items onChange.
  const [campaignName, setCampaignName] = useState<string | undefined>(undefined);

  // Pre-fill from campaign pinned items the first time we have a
  // campaign selected. We don't overwrite operator picks — only fold
  // pinned items into existing selections.
  useEffect(() => {
    if (!value.campaignId) {
      setCampaignName(undefined);
      return;
    }
    let cancelled = false;
    apiJson<{
      campaign: { kind: string | null; name?: string };
      pinnedProducts: { id: string }[];
      pinnedMedia: { id: string }[];
    }>(`/api/campaigns/${value.campaignId}`)
      .then(res => {
        if (cancelled) return;
        const pinnedProductIds = (res.pinnedProducts || []).map(p => p.id);
        const pinnedMediaIds   = (res.pinnedMedia    || []).map(m => m.id);
        const campaignKind     = res.campaign?.kind || null;
        setCampaignName(res.campaign?.name || undefined);
        // Stash kind so the wizard's Step 2 gate can let brand
        // campaigns proceed without picks. Always patch it (even
        // when null) so a campaign change resets the prior value.
        const patch: { campaignKind: string | null; productIds?: string[]; mediaIds?: string[] } = { campaignKind };
        // Pinned items are CAMPAIGN-level: they belong to the campaign, not to
        // any one product in it. Folding them in unconditionally meant clicking
        // "Generate Ads" on one product silently attached another product's
        // pinned photos to it — a Duffle Bag batch arriving with a t-shirt's
        // media, which is that t-shirt's picture in a Duffle Bag ad.
        //
        // Same shape of mistake as the campaign creative brief being fed to
        // every product's Director round: campaign-scoped data applied to a
        // product-scoped job.
        //
        // When the operator deep-linked from a product row they have already
        // named the subject of this batch, so the pins do not apply. They still
        // hydrate for the ordinary "pick a campaign, then choose" flow, where
        // pinning is exactly the point.
        if (!deepLinkedToProduct) {
          if (pinnedProductIds.length) patch.productIds = Array.from(new Set([...value.productIds, ...pinnedProductIds]));
          if (pinnedMediaIds.length)   patch.mediaIds   = Array.from(new Set([...value.mediaIds,   ...pinnedMediaIds]));
        } else if (pinnedProductIds.length || pinnedMediaIds.length) {
          console.info(
            `[wizard] deep-linked to a product — not folding in ${pinnedProductIds.length} pinned product(s) / ${pinnedMediaIds.length} pinned media from the campaign`
          );
        }
        onChange(patch);
      })
      .catch(() => { /* non-blocking — pinned hydration is best-effort */ });
    return () => { cancelled = true; };
    // Run once per campaignId change. Adding value.* into deps would
    // re-fire on every selection — not what we want.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.campaignId]);

  // Lift display rows for currently-selected ids into wizard state so
  // Step 4 can render names/thumbs without re-fetching. Preserves prior
  // summary entries for ids whose rows haven't hydrated yet (deep-link
  // / pagination) so the strip doesn't flicker empty mid-load.
  useEffect(() => {
    const prev = value.selectionSummary || { products: [], media: [] };
    const nextProducts = value.productIds.map(id => {
      const p = products.find(x => x.id === id);
      if (p) return { id: p.id, title: p.title || '', imageUrl: p.imageUrl || null };
      return prev.products.find(x => x.id === id) || null;
    }).filter((x): x is SelectionSummary['products'][number] => !!x);

    const nextMedia = value.mediaIds.map(id => {
      const m = media.find(x => x.id === id);
      if (m) return { id: m.id, thumbUrl: mediaThumbUrl(m) };
      return prev.media.find(x => x.id === id) || null;
    }).filter((x): x is SelectionSummary['media'][number] => !!x);

    const next: SelectionSummary = {
      campaignName,
      products: nextProducts,
      media:    nextMedia
    };
    // Skip no-op patches so we don't loop onChange → re-render → effect.
    if (JSON.stringify(next) === JSON.stringify(prev)) return;
    onChange({ selectionSummary: next });
    // value.selectionSummary intentionally omitted — we read it for
    // stale-entry preservation but only write when the derived next
    // differs; including it would re-fire after every successful patch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignName, value.productIds, value.mediaIds, products, media]);

  // Cross-direction lookups. When the operator selects one or more
  // media, fetch related products from each (deduped + tagged with
  // the seed mediaId so the X exclusion knows which pairing to drop);
  // same for products → related media. Cap each related ribbon to ~24
  // (was 12) so the per-tier groups don't all squeeze into one bucket.
  useEffect(() => {
    let cancelled = false;
    if (!value.mediaIds.length) { setRelatedProducts([]); return; }
    Promise.all(
      value.mediaIds.slice(0, 5).map(seedId =>
        apiJson<{ products: ProductRow[] }>(`/api/media/${seedId}/related-products`)
          .then(r => (r.products || []).map(p => ({ ...p, seedMediaId: seedId })))
          .catch(() => [] as ProductRow[])
      )
    ).then(lists => {
      if (cancelled) return;
      const seen = new Set(value.productIds.map(String));
      const flat: ProductRow[] = [];
      for (const list of lists) {
        for (const p of list) {
          // Dedupe by (seedMediaId, productId) so the same product
          // surfaced from two seed media still gets one tile per pair —
          // each pair is independently excludable.
          const key = `${p.seedMediaId}|${p.id}`;
          if (seen.has(key)) continue;
          seen.add(key);
          flat.push(p);
        }
      }
      setRelatedProducts(flat.slice(0, 24));
    });
    return () => { cancelled = true; };
  }, [value.mediaIds, value.productIds]);

  // Kind detectors + effective seed-product set. Declared here so the
  // relatedMedia useEffect below can fetch off the union (explicit
  // picks ∪ media-derived for brand-kind) rather than just
  // value.productIds.
  const isProductKind = value.campaignKind === 'product';
  const isBrandKind   = value.campaignKind === 'brand';
  const mediaDerivedProductIds = useMemo(() => {
    if (!isBrandKind) return [] as string[];
    return relatedProducts
      .filter(p => p.matchTier === 'product_match')
      .map(p => p.id);
  }, [isBrandKind, relatedProducts]);
  const expansionSeedProductIds = useMemo(() => {
    if (!isBrandKind) return value.productIds;
    return Array.from(new Set([...value.productIds, ...mediaDerivedProductIds]));
  }, [isBrandKind, value.productIds, mediaDerivedProductIds]);

  // Sum of `filteredOutByAdEligible` across all picked products' match
  // fetches. When > 0 and relatedMedia is empty, the picker can explain
  // why: the classifier flagged every matched post as promotional or
  // announcement and the adEligible gate dropped them.
  const [adEligibleFilteredCount, setAdEligibleFilteredCount] = useState(0);
  // Operator override — when true, the related-media fetch drops the
  // adEligible=1 gate and includes classifier-blocked posts. Useful
  // when the only matches for a product are flagged promotional but
  // the operator still wants to use them (e.g. picking a detect-
  // identified product whose source post is what created it).
  const [includeFilteredPromo, setIncludeFilteredPromo] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Fetch on the effective seed-product set so the brand-kind
    // media-seed branch picks up matched posts for media-derived
    // products (not just explicit picks). Product-kind / other kinds
    // fall back to value.productIds via expansionSeedProductIds's
    // own derivation.
    if (!expansionSeedProductIds.length) { setRelatedMedia([]); setAdEligibleFilteredCount(0); return; }
    const adEligibleParam = includeFilteredPromo ? '0' : '1';
    Promise.all(
      expansionSeedProductIds.slice(0, 5).map(seedId =>
        apiJson<{
          matches: { mediaId: string; matchTier: MatchTier | null; outcomeReasoning: string | null; winner: string | null; matchSource: string | null; catalogCombinedScore: number | null; media: MediaRow }[];
          filteredOutByAdEligible?: number;
        }>(`/api/catalog/${seedId}/matches?adEligible=${adEligibleParam}`)
          .then(r => ({
            seedId,
            rows: (r.matches || []).map(x => ({
              ...x.media,
              id: x.mediaId,
              seedProductId:        seedId,
              matchTier:            x.matchTier,
              outcomeReasoning:     x.outcomeReasoning,
              winner:               x.winner,
              matchSource:          x.matchSource,
              catalogCombinedScore: x.catalogCombinedScore
            })),
            filteredOut: r.filteredOutByAdEligible || 0
          }))
          .catch(() => ({ seedId, rows: [] as MediaRow[], filteredOut: 0 }))
      )
    ).then(results => {
      if (cancelled) return;
      const seen = new Set(value.mediaIds.map(String));
      const flat: MediaRow[] = [];
      let totalFiltered = 0;
      for (const r of results) {
        totalFiltered += r.filteredOut;
        for (const m of r.rows) {
          // Dedupe by (seedProductId, mediaId) — same media surfaced
          // from two seed products gets one tile per pair so each is
          // independently excludable.
          const key = `${m.seedProductId}|${m.id}`;
          if (seen.has(key)) continue;
          seen.add(key);
          flat.push(m);
        }
      }
      setAdEligibleFilteredCount(totalFiltered);
      setRelatedMedia(flat.slice(0, 24));
    });
    return () => { cancelled = true; };
  }, [expansionSeedProductIds, value.mediaIds, includeFilteredPromo]);

  // Promotional context — when the active campaign is promotional,
  // surface the operator-supplied offer details as a banner above the
  // picker so they stay oriented while choosing products/media. Lives
  // outside WizardSelections (read-only, doesn't ride into the
  // generate POST). Hydrated alongside the existing campaign fetch.
  type PromoContext = {
    kind:          string | null;
    discountType:  string | null;
    discountValue: number | null;
    discountCode:  string | null;
    rafflePrize:   string | null;
    raffleEntriesPerDollar: number | null;
    raffleDrawDate: string | null;
    endsAt:        string | null;
  };
  const [promoContext, setPromoContext] = useState<PromoContext | null>(null);
  useEffect(() => {
    if (!value.campaignId) { setPromoContext(null); return; }
    let cancelled = false;
    apiJson<{ campaign: { kind: string | null; promotionalDetails: Record<string, unknown> | null } }>(
      `/api/campaigns/${value.campaignId}`
    ).then(res => {
      if (cancelled) return;
      const c = res.campaign;
      if (c?.kind !== 'promotional' || !c?.promotionalDetails) {
        setPromoContext(null);
        return;
      }
      const p = c.promotionalDetails;
      setPromoContext({
        kind:          c.kind,
        discountType:  (p.discountType as string)  || null,
        discountValue: (p.discountValue as number) ?? null,
        discountCode:  (p.discountCode as string)  || null,
        rafflePrize:   (p.rafflePrize as string)   || null,
        raffleEntriesPerDollar: (p.raffleEntriesPerDollar as number) ?? null,
        raffleDrawDate: (p.raffleDrawDate as string) || null,
        endsAt:        (p.endsAt as string)        || null
      });
    }).catch(() => { if (!cancelled) setPromoContext(null); });
    return () => { cancelled = true; };
  }, [value.campaignId]);

  // Lifestyle-eligible media IDs for brand-kind only. The /api/brand/:id/
  // lifestyle-eligible-media-ids endpoint returns the Set of brand
  // Media whose shotType is in {lifestyle, on_model, unknown} OR is
  // unset/null (the wider "brand-campaign-appropriate" net). Used to
  // filter alt + UGC tiles in the brand-kind unified ribbon — heroes
  // stay visible regardless (they're the entry point to product
  // selection; if a product has only a studio hero, the operator can
  // still pick the product, the cartesian just won't have lifestyle
  // material for it).
  const [lifestyleEligibleIds, setLifestyleEligibleIds] = useState<Set<string> | null>(null);
  useEffect(() => {
    if (!brandId || value.campaignKind !== 'brand') { setLifestyleEligibleIds(null); return; }
    let cancelled = false;
    apiJson<{ mediaIds: string[] }>(`/api/brand/${encodeURIComponent(brandId)}/lifestyle-eligible-media-ids`)
      .then(r => { if (!cancelled) setLifestyleEligibleIds(new Set(r.mediaIds || [])); })
      .catch(() => { if (!cancelled) setLifestyleEligibleIds(new Set()); });
    return () => { cancelled = true; };
  }, [brandId, value.campaignKind]);

  // Brand-wide brand_match posts. Always fetched (independent of which
  // products/media are picked) — these are the seeds that ride along
  // for any ad-generate run regardless of product selection. Operator
  // can individually exclude any of them via the X overlay.
  const [brandMatches, setBrandMatches] = useState<MediaRow[]>([]);
  useEffect(() => {
    if (!brandId) { setBrandMatches([]); return; }
    let cancelled = false;
    apiJson<{ matches: Array<{
      mediaId: string;
      matchTier: MatchTier;
      outcome: string | null;
      outcomeReasoning: string | null;
      winner: string | null;
      matchSource: string | null;
      confidence: number | null;
      media: {
        externalId: string; fileType: 'image' | 'video'; fileUrl: string;
        source: string; permalink: string | null;
        creatorHandle: string | null; postedAt: string | null;
        likes: number | null; comments: number | null; saves: number | null;
        engagement: number | null; postType: string | null;
        shotType: string | null; contentNature: string | null;
        adReadiness: number | null;
        adSuitability: number | null; createdAt: string;
      };
    }> }>(`/api/brand/${encodeURIComponent(brandId)}/brand-matches?limit=24`)
      .then(r => {
        if (cancelled) return;
        const rows: MediaRow[] = (r.matches || []).map(x => ({
          id:                  x.mediaId,
          fileType:            x.media.fileType,
          fileUrl:             x.media.fileUrl,
          creatorHandle:       x.media.creatorHandle,
          primarySubjectLabel: null,
          postedAt:            x.media.postedAt,
          source:              x.media.source,
          seedProductId:       null,             // brand_match has no SKU seed
          matchTier:           x.matchTier,
          outcomeReasoning:    x.outcomeReasoning,
          winner:              x.winner,
          matchSource:         x.matchSource,
          catalogCombinedScore: x.confidence,
          likes:               x.media.likes,
          comments:            x.media.comments,
          saves:               x.media.saves,
          engagement:          x.media.engagement,
          postType:            x.media.postType,
          shotType:            x.media.shotType,
          contentNature:       x.media.contentNature,
          adReadiness:         x.media.adReadiness ?? x.media.adSuitability
        }));
        setBrandMatches(rows);
      })
      .catch(() => { if (!cancelled) setBrandMatches([]); });
    return () => { cancelled = true; };
  }, [brandId]);

  // Catalog imagery for the effective seed products — hero + alts with
  // imageMediaId when materialized. Used by the seed-selectable rail
  // (seedPicks, ordered pairs) so the operator can pick the video
  // reference stack; empty selection ⇒ backend defaults to hero+alt1+alt2.
  const [catalogImagery, setCatalogImagery] = useState<CatalogImageRow[]>([]);
  useEffect(() => {
    let cancelled = false;
    if (!expansionSeedProductIds.length) { setCatalogImagery([]); return; }
    Promise.all(
      expansionSeedProductIds.slice(0, 5).map(pid =>
        apiJson<{ product: {
          id: string;
          title: string | null;
          source: string | null;
          imageUrl: string | null;
          additionalImages: string[];
          imageMediaId: string | null;
          additionalImageMediaIds: string[];
          lastSyncedAt: string | null;
        };
          // Index-aligned with imageUrl / additionalImages. null = unmaterialised
          // or not yet classified, which is normal under deferred detect.
          imageShotType?: string | null;
          additionalImageShotTypes?: (string | null)[];
        }>(`/api/catalog/${pid}`)
          .then(r => {
            const out: CatalogImageRow[] = [];
            const title = r.product?.title || null;
            const productSource = r.product?.source || null;
            const lastSyncedAt = r.product?.lastSyncedAt || null;
            const altIds = r.product?.additionalImageMediaIds || [];
            const altShots = r.additionalImageShotTypes || [];
            if (r.product?.imageUrl) {
              out.push({
                productId: pid,
                productTitle: title,
                productSource,
                productLastSyncedAt: lastSyncedAt,
                imageUrl: r.product.imageUrl,
                imageMediaId: r.product.imageMediaId || null,
                role: 'hero',
                shotType: r.imageShotType ?? null
              });
            }
            (r.product?.additionalImages || []).forEach((url, i) => {
              if (url) out.push({
                productId: pid,
                productTitle: title,
                productSource,
                productLastSyncedAt: lastSyncedAt,
                imageUrl: url,
                imageMediaId: altIds[i] || null,
                role: 'alt',
                altIndex: i + 1,
                shotType: altShots[i] ?? null
              });
            });
            return out;
          })
          .catch(() => [] as CatalogImageRow[])
      )
    ).then(lists => {
      if (cancelled) return;
      setCatalogImagery(lists.flat());
    });
    return () => { cancelled = true; };
  }, [expansionSeedProductIds]);

  // Per-model reference-stack limits from the resolved video model.
  // Fall back to 7 / 3 if the fetch fails — never block the operator.
  const [refLimits, setRefLimits] = useState<{ max: number; defaultCount: number; model: string }>({
    max: 7, defaultCount: 3, model: ''
  });
  // Backend-resolved default-stack policy for both rails. Fallbacks hold the
  // historical hardcoded behaviour (video 3 / static 1, catalog only, no
  // preference) so a failed scaffold fetch changes nothing.
  const [refDefaults, setRefDefaults] = useState<{ video: { shotTypes: string[] }; image: ReferenceDefaultsPolicy }>({
    video: { shotTypes: [] },
    image: { count: IMAGE_QUEUE_DEFAULT_COUNT_FALLBACK, shotTypes: [] }
  });
  // Has the scaffold settled (resolved, failed, timed out, or nothing to fetch)?
  //
  // The pre-pick effect below is gated per product by seedDefaultsAppliedRef /
  // imageDefaultsAppliedRef, so it applies ONCE and never revisits. If
  // catalogImagery resolves before the scaffold, it would lock in the fallback
  // policy and silently ignore the configured one. Waiting closes that race —
  // and it closes the same pre-existing race on refLimits.defaultCount, where a
  // fast catalog fetch could pin 3 seeds before the model's real default arrived.
  //
  // ⚠️  It must never be able to withhold defaults INDEFINITELY. apiJson has no
  // timeout of its own, so a hung request would otherwise leave the operator
  // with an empty picker and no explanation — strictly worse than the old
  // behaviour, which always pre-picked something immediately. The watchdog below
  // bounds the wait; on expiry we proceed with the fallback policy.
  const [refDefaultsReady, setRefDefaultsReady] = useState(false);
  useEffect(() => {
    // Nothing to fetch — mark settled so the pre-pick isn't blocked forever.
    if (!value.campaignId) { setRefDefaultsReady(true); return; }
    let cancelled = false;
    const qp = new URLSearchParams({
      campaignId: value.campaignId,
      platformFormat: value.platformFormat,
      durationSec: String(value.videoDurationSec || 10)
    });
    if (value.productIds[0]) qp.set('productId', value.productIds[0]);
    // Watchdog — see refDefaultsReady. Bounds the pre-pick wait so a hung
    // scaffold degrades to the fallback policy instead of no defaults at all.
    const watchdog = window.setTimeout(() => {
      if (!cancelled) setRefDefaultsReady(true);
    }, 4000);
    apiJson<RefLimitsScaffold>(`/api/ads/veo-prompt-scaffold?${qp.toString()}`)
      .then(r => {
        if (cancelled) return;
        setRefLimits({
          max:          typeof r.maxReferenceImages === 'number' && r.maxReferenceImages > 0 ? r.maxReferenceImages : 7,
          defaultCount: typeof r.defaultReferenceCount === 'number' && r.defaultReferenceCount > 0 ? r.defaultReferenceCount : 3,
          model:        r.model || ''
        });
        // Only adopt a served policy that is actually well-formed; a partial or
        // malformed payload keeps the historical fallback rather than producing
        // a count of 0 (which would pre-pick nothing and look like a bug).
        const shots = (v: unknown, fb: string[]): string[] =>
          Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string').map(x => x.toLowerCase()) : fb;
        setRefDefaults(prev => ({
          video: { shotTypes: shots(r.referenceDefaults?.video?.shotTypes, prev.video.shotTypes) },
          image: {
            count: typeof r.referenceDefaults?.image?.count === 'number' && r.referenceDefaults.image.count >= 1
              ? r.referenceDefaults.image.count
              : prev.image.count,
            shotTypes: shots(r.referenceDefaults?.image?.shotTypes, prev.image.shotTypes)
          }
        }));
        setRefDefaultsReady(true);
        window.clearTimeout(watchdog);
      })
      .catch(() => {
        // Keep the 7/3 + historical policy fallback, but still unblock the
        // pre-pick — a scaffold outage must not leave the operator with no
        // defaults at all.
        if (!cancelled) setRefDefaultsReady(true);
      });
    return () => { cancelled = true; window.clearTimeout(watchdog); };
  }, [value.campaignId, value.platformFormat, value.videoDurationSec, value.productIds]);

  // Image-ad queue only when the run will actually produce image ads.
  // Reels (and any future video-only surface) keeps catalog images as seeds.
  const showImageQueue = useMemo(() => {
    return effectiveAdKinds(selectedFormatEntry, value.adKinds).includes('image');
  }, [selectedFormatEntry, value.adKinds]);
  // Mirror of showImageQueue for the ordered video-seed rail. Step4Generate
  // only sends seedMediaIds when video is in adKinds, so without this the rail
  // would render pre-picked and numbered on an image-only run and every pick
  // would be dropped on submit.
  const showVideoSeeds = useMemo(() => {
    return effectiveAdKinds(selectedFormatEntry, value.adKinds).includes('video');
  }, [selectedFormatEntry, value.adKinds]);

  // Video-ref prewarm. Once the operator is on a video-capable format with at
  // least one product selected, warm the backend's 9:16 reference-reframe
  // cache so the eventual paid run skips the cold outpaint stage (measured
  // 5m19s of a 12m38s run — the outpaints serialize on the discount tier).
  // Deliberately fired while the operator is still picking rather than at
  // catalog ingest: generative work stays scoped to products someone actually
  // intends to make a video from.
  //
  // Fire-and-forget by design — the backend answers 202 and warms in the
  // background, re-sends of an already-warm set are $0 (persistent reframe
  // cache + cross-process claim), and a failure here must never surface in
  // the wizard. The signature Set is traffic hygiene, not the spend guard.
  const videoRefPrewarmSentRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    // A scene reel never uses the standard video reference stack, so warming
    // its reframes would be work for nothing.
    if (!brandId || !showVideoSeeds || sceneReelMode || value.productIds.length === 0) return;

    const sortedIds = [...value.productIds].map(String).sort();
    const sig = `${brandId}|${sortedIds.join(',')}`;
    if (videoRefPrewarmSentRef.current.has(sig)) return;

    const timer = setTimeout(() => {
      // Mark inside the timer so a debounce cancelled mid-click-burst never
      // records a send that never left the client.
      if (videoRefPrewarmSentRef.current.has(sig)) return;
      videoRefPrewarmSentRef.current.add(sig);
      apiJson('/api/ads/video-ref-prewarm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brandId, productIds: sortedIds.slice(0, 12) }),
      }).catch(() => {});
    }, 1500);

    return () => clearTimeout(timer);
  }, [brandId, showVideoSeeds, sceneReelMode, value.productIds]);

  // Tracks which products have already received explicit default seed /
  // image-queue pre-picks. Prevents re-applying after the operator
  // deliberately clears a product's picks (empty is allowed; backend
  // still falls back to hero + first two alts).
  const seedDefaultsAppliedRef  = useRef<Set<string>>(new Set());
  const imageDefaultsAppliedRef = useRef<Set<string>>(new Set());

  // Exactly which mediaIds the image-queue pre-pick put into the selection, so
  // turning image ads OFF can take back its own picks and NOTHING else.
  //
  // WHY THIS EXISTS (owner, 2026-08-12). The pre-pick below only ever ADDED.
  // When the operator then switched the run to video-only, showImageQueue went
  // false, the queue disappeared from the UI — and the machine-chosen mediaId
  // stayed in the selection, still rendering as a blue "Media" chip the
  // operator never picked. That is not cosmetic: the backend reads ANY
  // non-empty mediaIds as a DELIBERATE operator override (universeTopN =
  // max(mediaIds.length, DIRECTOR_UNIVERSE_TOP_N) + restrictToMediaIds pins the
  // universe to exactly those picks), which suppresses the catalog-first
  // default this very file goes out of its way to preserve. So a pick nobody
  // made silently narrowed what the ads were built from, and if any static
  // surface was ticked again it became billable image generations.
  //
  // Tracked by id rather than "clear all mediaIds", because the operator's own
  // picks live in the same array and must survive untouched.
  const autoPickedMediaRef = useRef<Set<string>>(new Set());

  // Drop applied flags for products that left the expansion set so a
  // re-select re-pre-picks defaults.
  useEffect(() => {
    const live = new Set(expansionSeedProductIds.map(String));
    for (const id of Array.from(seedDefaultsAppliedRef.current)) {
      if (!live.has(id)) seedDefaultsAppliedRef.current.delete(id);
    }
    for (const id of Array.from(imageDefaultsAppliedRef.current)) {
      if (!live.has(id)) imageDefaultsAppliedRef.current.delete(id);
    }
  }, [expansionSeedProductIds]);

  // Explicit default pre-picks per product, in feed order (hero first, then
  // alts), materialised imageMediaId rows only. The operator can SEE and change
  // every default instead of inheriting an invisible backend fallback.
  //
  // The two rails have DIFFERENT defaults, because they feed different
  // pipelines:
  //   video  — defaultReferenceCount (3 distinct views; the backend repeats the
  //            primary as the closing ref, so the stack ships as 4). Matches the
  //            backend's own empty-means-default of hero + first two alts.
  //   static — refDefaults.image.count (IMAGE_DEFAULT_REFERENCE_COUNT, default 1):
  //            the catalog's first image, alone.
  // The static pre-pick is deliberately STRONGER than the backend's
  // empty-picks fallback rather than a mirror of it: that fallback trims a
  // shotType-ranked pool to DIRECTOR_UNIVERSE_TOP_N, and `imageRole:'hero'` is
  // only a within-tier tiebreak there (seededUniverseService rankMergedPool), so
  // a lifestyle alt can outrank the hero. Naming the hero here pins it.
  useEffect(() => {
    if (!catalogImagery.length) return;
    // Wait for the resolved policy — see refDefaultsReady.
    if (!refDefaultsReady) return;

    // Feed order preserved: catalogImagery is built hero-then-alts per product.
    // Rows (not just ids) so the configured shot-type preference can reorder
    // them per rail below — the two rails can be configured differently, so the
    // ordering has to happen per rail rather than once here.
    const byProductRows = new Map<string, CatalogImageRow[]>();
    const byProduct = new Map<string, string[]>();
    // Whether THIS product's catalog-first image (role 'hero', built from
    // CatalogProduct.imageUrl) is actually materialised. Load-bearing for the
    // image queue — see the guard at its pre-pick below.
    const firstIsMaterialised = new Map<string, boolean>();
    for (const img of catalogImagery) {
      const pid = String(img.productId);
      if (!firstIsMaterialised.has(pid)) {
        // catalogImagery is emitted hero-then-alts, so the FIRST row seen for a
        // product is the catalog's first image — materialised or not.
        firstIsMaterialised.set(pid, !!img.imageMediaId && img.role === 'hero');
      }
      if (!img.imageMediaId) continue;
      if (!byProduct.has(pid)) byProduct.set(pid, []);
      byProduct.get(pid)!.push(img.imageMediaId);
      if (!byProductRows.has(pid)) byProductRows.set(pid, []);
      byProductRows.get(pid)!.push(img);
    }

    const expansionSet = new Set(expansionSeedProductIds.map(String));
    let nextSeeds: SeedPick[] = (value.seedPicks || []).slice();
    let seedsChanged = false;
    let nextMedia = value.mediaIds.slice();
    let mediaChanged = false;
    const seedDefaultN = refLimits.defaultCount;

    for (const [pid, ids] of byProduct) {
      if (!expansionSet.has(pid)) continue;
      const idSet = new Set(ids);
      // Per-rail preference ordering over the same materialised rows. Both are
      // no-ops when the corresponding *_SHOT_TYPES env is unset (the default),
      // leaving pure catalog feed order — the owner's "first, second, third as
      // downloaded". idSet stays keyed off the unordered ids, so membership
      // checks are unaffected by either ordering.
      const rows = byProductRows.get(pid) || [];
      const seedIds  = orderByShotTypePreference(rows, refDefaults.video.shotTypes)
        .map(r => r.imageMediaId!).filter(Boolean);
      const imageIds = orderByShotTypePreference(rows, refDefaults.image.shotTypes)
        .map(r => r.imageMediaId!).filter(Boolean);

      if (!seedDefaultsAppliedRef.current.has(pid)) {
        const existing = nextSeeds.filter(p => p.productId === pid);
        // Same materialisation guard as the image queue below, and for the same
        // reason. `ids` holds materialised rows only, so when the hero has no
        // imageMediaId this slice is alt1+alt2+alt3 — a stack that EXCLUDES the
        // primary product image. Worse, any non-empty seedPicks is read by the
        // backend as a deliberate operator ordering, which suppresses its own
        // empty-means-default of hero + first two alts
        // (expandDeterministicVideo: `referenceMediaIds = []` → render derives
        // hero+alt1+alt2). So pre-picking here silently dropped the single most
        // important reference from every video. Empty is the safe state.
        //
        // A PARTIAL stack is the same bug one level down. The materialisation
        // guard above only asks whether the HERO landed; it says nothing about
        // the alts. `seedIds` holds materialised rows only, and alt
        // materialisation lags the hero — enqueueProductDetect short-circuits
        // once imageMediaId is set, and the alts are backfilled lazily by
        // GET /api/catalog/:id. So while they are still in flight this slice
        // yields [hero] ALONE, and because any non-empty seedPicks reads as a
        // deliberate ordering, the backend takes the ordered-only branch in
        // buildReferenceImages and skips seed+catalog assembly entirely —
        // shipping a ONE-image product video where the default is three.
        // Measured on prod 2026-08-17: 15 of 126 recent video ads carried
        // exactly one reference; "Isla Double Cloth Wide Leg Pant" had 7
        // materialised images and still shipped 1. Because the pre-pick is
        // one-shot per product it never revised itself when the alts arrived
        // seconds later.
        //
        // So pre-pick ONLY a full default stack. Anything short stays empty —
        // the state the backend already resolves to hero+alt1+alt2 — and the
        // product is deliberately NOT marked applied, so the alts landing while
        // the operator is still on Step 2 completes it then. A deliberate
        // operator pick is never overridden: `existing.length === 0` means they
        // have chosen nothing for this product yet, and once they own the
        // selection (or we placed a full stack) the product IS marked applied.
        const canPickFullStack =
          !!firstIsMaterialised.get(pid) && seedIds.length >= seedDefaultN;
        if (existing.length === 0 && canPickFullStack) {
          for (const mid of seedIds.slice(0, seedDefaultN)) {
            if (!nextSeeds.some(p => p.productId === pid && p.mediaId === mid)) {
              nextSeeds.push({ productId: pid, mediaId: mid });
              seedsChanged = true;
            }
          }
        }
        if (existing.length > 0 || canPickFullStack) {
          seedDefaultsAppliedRef.current.add(pid);
        }
      }

      if (showImageQueue && !imageDefaultsAppliedRef.current.has(pid)) {
        const existing = nextMedia.filter(id => idSet.has(id));
        // ONLY pre-pick when the catalog's FIRST image is genuinely materialised.
        //
        // ids holds materialised rows only, so ids[0] is the first image that has
        // an imageMediaId — NOT necessarily the catalog's first image. When hero
        // materialisation failed but alts landed, ids[0] is an ALT, and pre-picking
        // it is actively worse than pre-picking nothing: any non-empty mediaIds is
        // read by the backend as a deliberate operator override
        // (universeTopN = Math.max(mediaIds.length, DIRECTOR_UNIVERSE_TOP_N), and
        // restrictToMediaIds pins the universe to exactly those picks), which
        // SUPPRESSES the catalog-first default we want. Leaving mediaIds empty lets
        // the backend apply that default — which resolves to a catalog image and
        // never to UGC. Empty is the safe state here; a wrong pick is not.
        if (existing.length === 0 && firstIsMaterialised.get(pid)) {
          for (const mid of imageIds.slice(0, refDefaults.image.count)) {
            if (!nextMedia.includes(mid)) {
              nextMedia.push(mid);
              // Remember it was US, not the operator — see autoPickedMediaRef.
              autoPickedMediaRef.current.add(mid);
              mediaChanged = true;
            }
          }
        }
        imageDefaultsAppliedRef.current.add(pid);
      }
    }

    if (!seedsChanged && !mediaChanged) return;
    const patch: Partial<WizardSelections> = {};
    if (seedsChanged) patch.seedPicks = nextSeeds;
    if (mediaChanged) patch.mediaIds = nextMedia;
    onChange(patch);
    // onChange identity is unstable; seed/media arrays intentionally
    // omitted from deps so operator toggles don't re-enter — applied
    // refs gate each product to a single pre-pick pass.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogImagery, expansionSeedProductIds, refLimits.defaultCount, showImageQueue,
      refDefaultsReady, refDefaults]);

  // SYMMETRY FOR THE PRE-PICK ABOVE. When the run stops wanting image ads, hand
  // back exactly the mediaIds we auto-picked — never an operator's own pick.
  //
  // Without this the queue vanishes from the UI while its machine-chosen picks
  // stay in the payload, where the backend treats them as a deliberate operator
  // override (see autoPickedMediaRef). The operator cannot even un-tick them,
  // because the rail that renders the tick boxes is hidden by then.
  //
  // imageDefaultsAppliedRef is cleared alongside, so re-enabling image ads
  // re-applies the default rather than leaving the queue permanently empty.
  useEffect(() => {
    if (showImageQueue) return;
    if (!autoPickedMediaRef.current.size) return;
    const auto = autoPickedMediaRef.current;
    const survivors = value.mediaIds.filter(id => !auto.has(id));
    autoPickedMediaRef.current = new Set();
    imageDefaultsAppliedRef.current.clear();
    if (survivors.length !== value.mediaIds.length) onChange({ mediaIds: survivors });
    // onChange identity is unstable and value.mediaIds is rewritten by this very
    // call; gating on showImageQueue + a non-empty auto set makes it idempotent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showImageQueue]);

  // Fetch concept-usage for every visible product + media (including
  // catalog alt mediaIds) so the ribbon tiles paint the dot row +
  // ad-count badge. Re-fires whenever the visible lists change.
  // Placed after catalogImagery is declared (TDZ would fire if this
  // sat above its useState).
  useEffect(() => {
    if (!brandId) return;
    const productIds = products.map(p => p.id).filter(Boolean);
    const altMediaIds = catalogImagery.map(c => c.imageMediaId).filter((id): id is string => !!id);
    const mediaIds   = Array.from(new Set([
      ...media.map(m => m.id).filter(Boolean),
      ...altMediaIds
    ]));
    if (!productIds.length && !mediaIds.length) {
      setSeedUsage(new Map());
      return;
    }
    let cancelled = false;
    const qp = new URLSearchParams({ brandId });
    if (productIds.length) qp.set('productIds', productIds.join(','));
    if (mediaIds.length)   qp.set('mediaIds',   mediaIds.join(','));
    if (value.campaignKind) qp.set('campaignKind', value.campaignKind);
    apiJson<SeedUsageResp>(`/api/seeds/usage?${qp.toString()}`)
      .then(res => {
        if (cancelled) return;
        const m = new Map<string, SeedUsage>();
        for (const s of (res.seeds || [])) {
          m.set(s.seedId, {
            adCount:           s.adCount,
            conceptsTotal:     s.conceptsTotal,
            conceptsUsed:      s.conceptsUsed,
            conceptsRemaining: s.conceptsRemaining
          });
        }
        setSeedUsage(m);
      })
      .catch(() => { /* silent — usage chrome is non-critical */ });
    return () => { cancelled = true; };
  }, [brandId, products, media, catalogImagery, value.campaignKind]);

  const productSet = useMemo(() => new Set(value.productIds), [value.productIds]);
  const mediaSet   = useMemo(() => new Set(value.mediaIds),   [value.mediaIds]);
  const excludedSet = useMemo(() => new Set(value.excludedPairings), [value.excludedPairings]);
  // ORDERED catalog seed picks — indexOf is the 1-based badge source.
  // Must stay an array (never a Set): pick order = LLM reference order.
  // In scene-reel mode the rail reads and writes the scene list instead
  // (pick order = scene order); the standard list is left untouched.
  const seedPicks: SeedPick[] = (sceneReelMode ? value.sceneSeedPicks : value.seedPicks) || [];
  const writeSeedPicks = (next: SeedPick[]) =>
    onChange(sceneReelMode ? { sceneSeedPicks: next } : { seedPicks: next });

  // Badge / count derive straight from the PAIRS, so no mediaId → productId map
  // is needed any more. That map could only hold one product per media, which
  // silently lost an assignment when the same post was matched to two selected
  // products (last writer won).
  //
  // 1-based pick order WITHIN the product. 0 when not picked for it.
  const seedBadgeFor = (productId: string, mediaId: string): number =>
    seedPicks.filter(p => p.productId === String(productId))
             .findIndex(p => p.mediaId === mediaId) + 1;
  const seedCountForProduct = (productId: string): number =>
    seedPicks.filter(p => p.productId === String(productId)).length;

  // Tier-grouped related media for the expansion view. Filters the
  // single relatedMedia list by matchTier so each block can be shown
  // independently behind its expand button.
  const productMatchedRelated  = useMemo(() => relatedMedia.filter(m => m.matchTier === 'product_match'),    [relatedMedia]);
  const categoryMatchedRelated = useMemo(() => relatedMedia.filter(m => m.matchTier === 'product_category'), [relatedMedia]);

  const toggleProduct = (id: string) => {
    if (productSet.has(id)) {
      // Deselecting — prune this product's catalog seed picks AND any
      // catalog imageMediaIds that were queued for image ads, so the
      // ordered stack + image queue stay honest. Also drop the
      // defaults-applied flags so a re-select re-pre-picks.
      const removedCatalogIds = new Set(
        catalogImagery
          .filter(im => String(im.productId) === id && im.imageMediaId)
          .map(im => im.imageMediaId as string)
      );
      seedDefaultsAppliedRef.current.delete(id);
      imageDefaultsAppliedRef.current.delete(id);
      onChange({
        productIds: value.productIds.filter(x => x !== id),
        ...(removedCatalogIds.size
          ? {
              seedPicks: (value.seedPicks || []).filter(p => p.productId !== id),
              mediaIds: value.mediaIds.filter(mid => !removedCatalogIds.has(mid))
            }
          : {}),
        // Scene picks are dropped with their product whether or not catalog
        // imagery has loaded — a stale pick would 400 the scene-reel request.
        ...((value.sceneSeedPicks || []).some(p => p.productId === id)
          ? { sceneSeedPicks: value.sceneSeedPicks.filter(p => p.productId !== id) }
          : {})
      });
    } else {
      onChange({ productIds: [...value.productIds, id] });
    }
  };
  const toggleMedia = (id: string) => {
    // Once the operator touches a pick it is THEIRS — drop it from the
    // auto-picked set so the video-only prune above can never take it back.
    // Applies to both directions: re-ticking an id we pre-picked adopts it,
    // and un-ticking it removes it from the selection outright.
    autoPickedMediaRef.current.delete(id);
    onChange({
      mediaIds: mediaSet.has(id)
        ? value.mediaIds.filter(x => x !== id)
        : [...value.mediaIds, id]
    });
  };
  // Toggle a (productId, mediaId) pairing in the exclusion set. Used
  // by the X overlay on every related-ribbon tile. Pure additive —
  // doesn't change picks, only what fans out from them.
  const toggleExclude = (productId: string | null, mediaId: string) => {
    const k = pairingKey(productId, mediaId);
    onChange({
      excludedPairings: excludedSet.has(k)
        ? value.excludedPairings.filter(x => x !== k)
        : [...value.excludedPairings, k]
    });
  };
  // Toggle a catalog-product Media id into the ordered seed stack.
  // Select = append (preserves pick order); deselect = splice (re-numbers).
  // Distinct from mediaIds (UGC). Null imageMediaId tiles never call this.
  const toggleSeed = (productId: string, mediaId: string) => {
    const pid = String(productId);
    const idx = seedPicks.findIndex(p => p.productId === pid && p.mediaId === mediaId);
    if (idx >= 0) {
      const next = seedPicks.slice();
      next.splice(idx, 1);
      writeSeedPicks(next);
    } else {
      // A scene reel has a hard per-product cap (the backend 400s past it);
      // the full tiles are disabled, this is the backstop.
      if (sceneReelMode && seedCountForProduct(pid) >= scenePickCap) return;
      writeSeedPicks([...seedPicks, { productId: pid, mediaId }]);
    }
  };

  const totalSelected = value.productIds.length + value.mediaIds.length;
  const excludedCount = value.excludedPairings.length;

  // Did the operator ARRIVE here with products already chosen? Product Ads
  // deep-links /generate-ads?productIds=… from both the per-row "Generate Ads"
  // action and the bulk select, and index.tsx strips the param once consumed —
  // so this is captured on first render rather than read from the URL.
  //
  // It matters because the heading otherwise says "Pick products to make ads
  // for" to someone who just picked a product on the previous screen, which
  // reads as a do-over ("why am I choosing products again?") rather than an
  // invitation to add more.
  const arrivedWithProductsRef = useRef<boolean | null>(null);
  if (arrivedWithProductsRef.current === null) {
    arrivedWithProductsRef.current = value.productIds.length > 0;
  }
  const arrivedWithProducts = arrivedWithProductsRef.current === true;
  const preSelectedCount = value.productIds.length;

  // Kind-aware step heading + helper. The inner ribbon sections used to
  // duplicate this — operator saw two competing headlines ("Pick
  // products + media" up top, "Pick the products to make ads for"
  // inside). The inner SectionHeaders for the PRIMARY ribbon were
  // dropped; this is the single source of truth.
  const stepHeading = isBrandKind
    ? 'Pick media for your brand ads'
    : isProductKind
      ? (arrivedWithProducts
          ? `Making ads for ${preSelectedCount} product${preSelectedCount === 1 ? '' : 's'}`
          : 'Pick products to make ads for')
      : 'Pick products + media';
  const stepHelper = isBrandKind
    ? `Sorted by ad-fit — lifestyle, evergreen, and brand-only posts first. Tap a tile to queue it. ${totalSelected > 0 ? `${totalSelected} queued.` : ''}`
    : isProductKind
      ? (arrivedWithProducts
          ? `Add more products below if you want ads for them too — one ad set per SKU. ${totalSelected > 0 ? `${totalSelected} selected.` : ''}`
          : `Each tile is one SKU. Pick as many as you want — the wizard generates ads for each. ${totalSelected > 0 ? `${totalSelected} selected.` : ''}`)
      : `Each product or media seeds creatives. The render path caps at 6 ads per run, so 1–2 picks usually fills the batch. ${totalSelected > 0 ? `${totalSelected} selected.` : ''}`;
  return (
    <StepShell heading={stepHeading} helper={stepHelper}>
      <VStack align="stretch" spacing={6}>
        {promoContext && <PromoContextBanner ctx={promoContext} />}

        {/* Selected items summary */}
        {totalSelected > 0 && (
          <Box>
            <Text fontSize="xs" fontWeight="800" color="brand.muted" textTransform="uppercase" letterSpacing="0.06em" mb={2}>
              Selected ({totalSelected})
            </Text>
            <Wrap spacing={2}>
              {value.productIds.map(id => {
                const p = products.find(x => x.id === id);
                return (
                  <WrapItem key={`p-${id}`}>
                    <SelectionChip
                      tone="purple"
                      kindLabel="Product"
                      label={p?.title || id.slice(-6)}
                      thumbSrc={p?.imageUrl || null}
                      onRemove={() => toggleProduct(id)}
                    />
                  </WrapItem>
                );
              })}
              {value.mediaIds.map(id => {
                const m = media.find(x => x.id === id);
                const thumbSrc = m
                  ? (m.fileType === 'video' && m.fileUrl.includes('/video/upload/')
                      ? m.fileUrl.replace('/video/upload/', '/video/upload/so_0/').replace(/\.(mp4|mov|webm|m4v)(\?.*)?$/i, '.jpg$2')
                      : m.fileUrl)
                  : null;
                return (
                  <WrapItem key={`m-${id}`}>
                    <SelectionChip
                      tone="blue"
                      kindLabel="Media"
                      label={m?.primarySubjectLabel || m?.creatorHandle || id.slice(-6)}
                      thumbSrc={thumbSrc}
                      onRemove={() => toggleMedia(id)}
                    />
                  </WrapItem>
                );
              })}
              <WrapItem>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => {
                    seedDefaultsAppliedRef.current.clear();
                    imageDefaultsAppliedRef.current.clear();
                    onChange({ productIds: [], mediaIds: [], seedPicks: [] });
                  }}
                >
                  Clear all
                </Button>
              </WrapItem>
            </Wrap>
          </Box>
        )}

        {/* Product search + pagination. A catalog beyond PRODUCT_PAGE_SIZE
            used to be simply unreachable — no search box, no "load more",
            exactly 50 (now up to 100) tiles rendered with nothing to indicate
            more existed. `q` searches title/description server-side
            (routes/catalog.js); Load more pages in the rest via `offset`. */}
        <Box>
          <HStack spacing={3} align="center" flexWrap="wrap">
            <Input
              size="sm"
              maxW="280px"
              placeholder="Search products by name…"
              value={productSearchInput}
              onChange={e => setProductSearchInput(e.target.value)}
              bg="white"
              aria-label="Search products"
            />
            <Text fontSize="11px" color="brand.muted">
              {loadingP && products.length === 0
                ? 'Loading products…'
                : productTotal > products.length
                  ? `Showing ${products.length} of ${productTotal} product${productTotal === 1 ? '' : 's'}`
                  : `${productTotal} product${productTotal === 1 ? '' : 's'}${productQuery ? ' matching search' : ''}`}
            </Text>
            {productHasMore && (
              <Button
                size="xs"
                variant="outline"
                onClick={loadMoreProducts}
                isLoading={loadingMoreP}
                loadingText="Loading…"
              >
                Load {Math.min(PRODUCT_PAGE_SIZE, Math.max(productTotal - products.length, 0))} more
              </Button>
            )}
          </HStack>
        </Box>

        {brandId && (
          <PrepareCatalogBanner
            brandId={brandId}
            preparingCount={products.filter(p => p.pickerBlockReason === 'materializing').length}
            onPrepared={() => setCatalogRefetchNonce(n => n + 1)}
          />
        )}

        {isBrandKind ? (
          // ── Brand-kind: collapsed two-ribbon layout ──
          <BrandUnifiedView
            products={products}
            media={media}
            productSet={productSet}
            mediaSet={mediaSet}
            excludedSet={excludedSet}
            toggleProduct={toggleProduct}
            toggleMedia={toggleMedia}
            toggleExclude={toggleExclude}
            relatedProducts={relatedProducts}
            productMatchedRelated={productMatchedRelated}
            categoryMatchedRelated={categoryMatchedRelated}
            brandMatches={brandMatches}
            catalogImagery={catalogImagery}
            loadingP={loadingP}
            loadingM={loadingM}
            includeCategoryMatched={value.includeCategoryMatched}
            includeBrandMatched={value.includeBrandMatched}
            onTierToggle={(field, next) => onChange({ [field]: next } as Partial<WizardSelections>)}
            anyPicks={value.productIds.length + value.mediaIds.length > 0}
            lifestyleEligibleIds={lifestyleEligibleIds}
            seedUsage={seedUsage}
          />
        ) : isProductKind ? (
          // ── Product-kind: hero ribbon + unified Media Related section ──
          <ProductKindView
            products={products}
            productSet={productSet}
            mediaSet={mediaSet}
            excludedSet={excludedSet}
            toggleProduct={toggleProduct}
            toggleMedia={toggleMedia}
            toggleExclude={toggleExclude}
            catalogImagery={catalogImagery}
            productMatchedRelated={productMatchedRelated}
            categoryMatchedRelated={categoryMatchedRelated}
            brandMatches={brandMatches}
            loadingP={loadingP}
            productQuery={productQuery}
            catalogSize={catalogSize}
            includeCategoryMatched={value.includeCategoryMatched}
            includeBrandMatched={value.includeBrandMatched}
            onTierToggle={(field, next) => onChange({ [field]: next } as Partial<WizardSelections>)}
            anyProductPicked={value.productIds.length > 0}
            adEligibleFilteredCount={adEligibleFilteredCount}
            includeFilteredPromo={includeFilteredPromo}
            onToggleFilteredPromo={() => setIncludeFilteredPromo(v => !v)}
            seedUsage={seedUsage}
            showVideoSeeds={showVideoSeeds && !sceneReelMode}
            onToggleSeed={toggleSeed}
            seedBadgeFor={seedBadgeFor}
          />
        ) : (
          // ── Other kinds (promotional, unset): existing browse-everything layout ──
          <>
            <SectionHeader title="Products" subtitle="Click to feature in generated ads" />
            <RibbonPicker
              items={products}
              getId={p => p.id}
              selectedIds={productSet}
              onToggle={toggleProduct}
              loading={loadingP}
              emptyHint={productRibbonEmptyHint(productQuery, catalogSize)}
              render={(p, selected, onClick) => (
                <ProductTile product={p} selected={selected} onClick={onClick} usage={seedUsage.get(p.id)} />
              )}
            />

            {relatedProducts.length > 0 && (
              <RelatedTierBlocks
                heading="Products that will pair with your selected media"
                tiers={groupByTier(relatedProducts)}
                renderTile={(p, key) => {
                  const excluded = excludedSet.has(key);
                  return (
                    <ExcludeWrapper
                      excluded={excluded}
                      onExclude={() => toggleExclude(p.id, p.seedMediaId || '')}
                      reason={p.outcomeReasoning}
                      score={p.catalogCombinedScore}
                      winner={p.winner}
                      source={p.matchSource}
                    >
                      <ProductTile product={p} selected={productSet.has(p.id)} onClick={() => toggleProduct(p.id)} usage={seedUsage.get(p.id)} />
                    </ExcludeWrapper>
                  );
                }}
                keyFor={(p) => pairingKey(p.id, p.seedMediaId || '')}
              />
            )}

            <SectionHeader title="Media" subtitle="Click to feature creator content in ads" />
            <RibbonPicker
              items={media}
              getId={m => m.id}
              selectedIds={mediaSet}
              onToggle={toggleMedia}
              loading={loadingM}
              emptyHint="No media yet — connect Instagram or upload manually."
              render={(m, selected, onClick) => (
                <MediaTile media={m} selected={selected} onClick={onClick} usage={seedUsage.get(m.id)} />
              )}
            />

            {relatedMedia.length > 0 && (
              <RelatedTierBlocks
                heading="Posts that will pair with your selected products"
                tiers={groupByTier(relatedMedia)}
                renderTile={(m, key) => {
                  const excluded = excludedSet.has(key);
                  return (
                    <ExcludeWrapper
                      excluded={excluded}
                      onExclude={() => toggleExclude(m.seedProductId || null, m.id)}
                      reason={m.outcomeReasoning}
                      score={m.catalogCombinedScore}
                      winner={m.winner}
                      source={m.matchSource}
                      stats={{ likes: m.likes ?? null, comments: m.comments ?? null, saves: m.saves ?? null }}
                    >
                      <MediaTile media={m} selected={mediaSet.has(m.id)} onClick={() => toggleMedia(m.id)} />
                    </ExcludeWrapper>
                  );
                }}
                keyFor={(m) => pairingKey(m.seedProductId || null, m.id)}
              />
            )}

            {brandMatches.length > 0 && (
              <Box>
                <SectionHeader
                  title="Brand-only posts"
                  subtitle={`Posts matched to the brand without identifying a specific product. These ride along on every run unless excluded. (${brandMatches.length})`}
                  tone="muted"
                />
                <RibbonPicker
                  items={brandMatches}
                  getId={m => m.id}
                  selectedIds={mediaSet}
                  onToggle={toggleMedia}
                  render={(m) => {
                    const key = pairingKey(null, m.id);
                    const excluded = excludedSet.has(key);
                    return (
                      <ExcludeWrapper
                        excluded={excluded}
                        onExclude={() => toggleExclude(null, m.id)}
                        reason={m.outcomeReasoning}
                        score={m.catalogCombinedScore}
                        winner={m.winner}
                        source={m.matchSource}
                        stats={{ likes: m.likes ?? null, comments: m.comments ?? null, saves: m.saves ?? null }}
                      >
                        <MediaTile media={m} selected={mediaSet.has(m.id)} onClick={() => toggleMedia(m.id)} />
                      </ExcludeWrapper>
                    );
                  }}
                />
              </Box>
            )}
          </>
        )}

        {/* Per-product catalog sections — video seeds (ordered, numbered
            per product) + optional image-ad queue (checkmarks). Replaces
            the old flat UnifiedRibbon ALT tiles + global CatalogSeedRail
            so operators can see which images belong to which product. */}
        {catalogImagery.length > 0 && (
          <ProductCatalogSections
            products={products}
            catalogImagery={catalogImagery}
            expansionSeedProductIds={expansionSeedProductIds}
            mediaSet={mediaSet}
            onToggleSeed={toggleSeed}
            seedBadgeFor={seedBadgeFor}
            seedCountForProduct={seedCountForProduct}
            onToggleImageQueue={toggleMedia}
            showImageQueue={showImageQueue}
            showVideoSeeds={showVideoSeeds}
            maxReferenceImages={refLimits.max}
            defaultReferenceCount={refLimits.defaultCount}
            modelLabel={refLimits.model}
            scenePickCap={sceneReelMode ? scenePickCap : null}
          />
        )}

        {excludedCount > 0 && (
          <HStack pt={1}>
            <Badge colorScheme="orange" variant="subtle" fontSize="10px">
              {excludedCount} pairing{excludedCount === 1 ? '' : 's'} excluded
            </Badge>
            <Button
              size="xs"
              variant="ghost"
              onClick={() => onChange({ excludedPairings: [] })}
            >
              Clear exclusions
            </Button>
          </HStack>
        )}
      </VStack>
    </StepShell>
  );
}

// Per-product catalog sections. One block per expansion-seed product
// that has catalog imagery. Video seeds use the flat ordered
// seedPicks pair list (backend groups by the pair's productId, no
// metadata.catalogProductId) — badges number per-product so the
// operator sees a meaningful 1..N stack, not a global 1..12.
// Image-ad queue (mediaIds checkmarks) only renders when the run will
// produce image ads.
function ProductCatalogSections({
  products,
  catalogImagery,
  expansionSeedProductIds,
  mediaSet,
  onToggleSeed,
  seedBadgeFor,
  seedCountForProduct,
  onToggleImageQueue,
  showImageQueue,
  showVideoSeeds,
  maxReferenceImages,
  defaultReferenceCount,
  modelLabel,
  scenePickCap
}: {
  products:                 ProductRow[];
  catalogImagery:           CatalogImageRow[];
  expansionSeedProductIds:  string[];
  mediaSet:                 Set<string>;
  onToggleSeed:             (productId: string, mediaId: string) => void;
  // Shared with the related-media rail. Must NOT be recomputed locally from
  // catalogImagery alone: a related pick for this product occupies a position
  // in the flat ordered array too, so local numbering would disagree with what
  // expandDeterministicVideo actually sends to the model.
  seedBadgeFor:             (productId: string, mediaId: string) => number;
  seedCountForProduct:      (productId: string) => number;
  onToggleImageQueue:       (mediaId: string) => void;
  showImageQueue:           boolean;
  showVideoSeeds:           boolean;
  maxReferenceImages:       number;
  defaultReferenceCount:    number;
  modelLabel:               string;
  // Non-null in scene-reel mode: the rail is "preferred scene images", capped
  // at this many per product, and the standard video-seed copy does not apply.
  scenePickCap:             number | null;
}) {
  const productById = useMemo(() => {
    const m = new Map<string, ProductRow>();
    for (const p of products) m.set(String(p.id), p);
    return m;
  }, [products]);

  // Group imagery by product, ordered by expansionSeedProductIds so the
  // section list follows pick/seed order rather than fetch completion.
  const sections = useMemo(() => {
    const byProduct = new Map<string, CatalogImageRow[]>();
    for (const img of catalogImagery) {
      const pid = String(img.productId);
      if (!byProduct.has(pid)) byProduct.set(pid, []);
      byProduct.get(pid)!.push(img);
    }
    const ordered: Array<{ productId: string; images: CatalogImageRow[] }> = [];
    const seen = new Set<string>();
    for (const pid of expansionSeedProductIds.map(String)) {
      const images = byProduct.get(pid);
      if (!images?.length) continue;
      ordered.push({ productId: pid, images });
      seen.add(pid);
    }
    // Any imagery that arrived for a product not in the expansion list
    // (shouldn't normally happen) still surfaces so nothing is orphaned.
    for (const [pid, images] of byProduct) {
      if (seen.has(pid)) continue;
      ordered.push({ productId: pid, images });
    }
    return ordered;
  }, [catalogImagery, expansionSeedProductIds]);

  if (!sections.length) return null;

  const modelShort = modelLabel
    ? modelLabel.replace(/\/.*$/, '') // "grok-imagine/image-to-video" → "grok-imagine"
    : 'this model';
  const overCapCaption =
    `Only the first ${maxReferenceImages} are sent — ${modelShort} accepts ` +
    `${maxReferenceImages} reference image${maxReferenceImages === 1 ? '' : 's'}.`;

  return (
    <VStack align="stretch" spacing={4}>
      <SectionHeader
        title="Catalog images"
        subtitle={
          // Describe what IS shown rather than asserting why. The old copy said
          // "this format is video-only", which is wrong whenever the operator
          // simply chose one kind on a format that supports both (e.g. Feed).
          scenePickCap != null
            ? `Per product: up to ${scenePickCap} preferred scene images, in scene order. Leave empty and the reel picks the best lifestyle images itself.`
          : showImageQueue && showVideoSeeds
            ? `Per product: ordered video seeds (first ${defaultReferenceCount} catalog images) + image-ad queue (hero only). Add more to either.`
          : showVideoSeeds
            ? `Per product: ordered video seeds — #1 is the primary seed. Defaults: first ${defaultReferenceCount} catalog images.`
            : `Per product: image-ad queue. Defaults: the hero image only.`
        }
        tone="muted"
      />
      {sections.map(({ productId, images }) => {
        const product = productById.get(productId);
        const title =
          product?.title
          || images.find(i => i.productTitle)?.productTitle
          || productId.slice(-6);
        const thumb =
          product?.imageUrl
          || images.find(i => i.role === 'hero')?.imageUrl
          || images[0]?.imageUrl
          || null;

        // Count/order come from the SHARED map so related-media picks for this
        // product are included — see seedBadgeFor's comment.
        const seedCount = seedCountForProduct(productId);
        const pickCap = scenePickCap ?? maxReferenceImages;
        // Scene picks are capped at selection time, so nothing is ever over.
        const overCap = scenePickCap == null && seedCount > maxReferenceImages;
        const sceneFull = scenePickCap != null && seedCount >= scenePickCap;

        return (
          <Box
            key={productId}
            borderWidth="1px"
            borderColor="brand.border"
            borderRadius="md"
            bg="brand.surface"
            p={3}
          >
            <HStack spacing={3} mb={3}>
              {thumb ? (
                <Image
                  src={thumb}
                  alt={title}
                  w="36px" h="36px"
                  objectFit="cover"
                  borderRadius="md"
                  flexShrink={0}
                />
              ) : (
                <Box w="36px" h="36px" borderRadius="md" bg="gray.100" flexShrink={0} />
              )}
              <Text fontSize="sm" fontWeight="800" color="brand.ink" noOfLines={1}>
                {title}
              </Text>
            </HStack>

            {/* Video seeds — ordered, numbered per product. Gated on video
                kinds for the same reason the image queue is gated on image
                kinds: Step4Generate only POSTs seedMediaIds when video is in
                adKinds, so on an image-only run every numbered pick here would
                be silently discarded. A pre-selected, numbered picker that
                does nothing is worse than no picker at all. */}
            {showVideoSeeds && (<>
            <HStack justify="space-between" align="baseline" mb={1.5}>
              <Text fontSize="11px" fontWeight="700" color="brand.muted" textTransform="uppercase" letterSpacing="0.05em">
                {scenePickCap != null ? 'Preferred scene images — in scene order' : 'Video seeds — ordered'}
              </Text>
              <Text fontSize="11px" color="brand.muted" fontWeight="600">
                {seedCount} of {pickCap} picked
              </Text>
            </HStack>
            <Box overflowX="auto" overflowY="hidden" pb={2} sx={RIBBON_SCROLL_SX}>
              <HStack spacing={3} align="stretch" minW="min-content">
                {images.map((img, idx) => {
                  const mediaId = img.imageMediaId;
                  const badge = mediaId ? seedBadgeFor(productId, mediaId) : 0;
                  const selected = badge > 0;
                  // A full scene list only lets the operator remove picks.
                  const selectable = !!mediaId && (selected || !sceneFull);
                  // Grey (don't block) tiles whose order exceeds the model cap.
                  const greyed = selected && badge > maxReferenceImages;
                  const tileKey = mediaId || `pending-${productId}-${img.role}-${img.altIndex ?? 0}-${idx}`;
                  return (
                    <Box key={`seed-${tileKey}`} flexShrink={0} w="120px">
                      <Tooltip
                        label={
                          !mediaId
                            ? 'image still processing'
                            : scenePickCap != null
                              ? (selected
                                  ? `Scene image #${badge} — click to remove`
                                  : selectable
                                    ? 'Click to prefer for a scene'
                                    : `Up to ${scenePickCap} per product — remove one first`)
                              : (selected ? `Seed #${badge} — click to remove` : 'Click to add as video seed')
                        }
                        hasArrow
                        openDelay={300}
                      >
                        <Box
                          as={selectable ? 'button' : 'div'}
                          onClick={selectable ? () => onToggleSeed(productId, mediaId!) : undefined}
                          position="relative"
                          w="120px" h="120px"
                          borderRadius="md"
                          overflow="hidden"
                          bg="gray.100"
                          borderWidth="2px"
                          borderColor={selected ? 'rsViolet.400' : 'brand.border'}
                          cursor={selectable ? 'pointer' : 'not-allowed'}
                          opacity={selectable ? (greyed ? 0.45 : 1) : 0.55}
                          transition="all 120ms"
                          boxShadow={selected && !greyed ? '0 0 0 3px rgba(122,53,232,0.18)' : undefined}
                          _hover={selectable ? { borderColor: selected ? 'rsViolet.400' : 'gray.300' } : undefined}
                        >
                          <img
                            src={img.imageUrl}
                            alt={`${title} ${img.role}${img.altIndex ? ` ${img.altIndex}` : ''}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            loading="lazy"
                          />
                          {selected && (
                            <Badge
                              position="absolute" top="4px" right="4px"
                              fontSize="10px"
                              colorScheme={greyed ? 'gray' : 'purple'}
                              variant="solid"
                              borderRadius="full"
                              minW="20px"
                              textAlign="center"
                            >
                              {badge}
                            </Badge>
                          )}
                          <Badge
                            position="absolute" top="4px" left="4px"
                            fontSize="9px"
                            colorScheme={img.role === 'hero' ? 'green' : 'gray'}
                            variant="solid"
                          >
                            {img.role === 'hero' ? 'PRIMARY' : `ALT ${img.altIndex ?? ''}`}
                          </Badge>
                        </Box>
                      </Tooltip>
                    </Box>
                  );
                })}
              </HStack>
            </Box>
            {seedCount === 0 && (
              <Text fontSize="11px" color="brand.muted" mt={0.5}>
                {scenePickCap != null
                  ? 'No picks — the reel chooses its own scene images.'
                  : 'No picks — the primary image + first two alts will be used.'}
              </Text>
            )}
            {overCap && (
              <Text fontSize="11px" color="orange.600" mt={0.5}>
                {overCapCaption}
              </Text>
            )}
            </>)}

            {/* Image-ad queue — checkmarks, no ordering; only when image kinds active */}
            {showImageQueue && (
              <Box mt={3}>
                <HStack justify="space-between" align="baseline" mb={1.5}>
                  <Text fontSize="11px" fontWeight="700" color="brand.muted" textTransform="uppercase" letterSpacing="0.05em">
                    Image-ad queue
                  </Text>
                  <Text fontSize="11px" color="brand.muted" fontWeight="600">
                    {images.filter(i => i.imageMediaId && mediaSet.has(i.imageMediaId)).length} queued
                  </Text>
                </HStack>
                <Box overflowX="auto" overflowY="hidden" pb={2} sx={RIBBON_SCROLL_SX}>
                  <HStack spacing={3} align="stretch" minW="min-content">
                    {images.map((img, idx) => {
                      const mediaId = img.imageMediaId;
                      const selectable = !!mediaId;
                      const selected = selectable ? mediaSet.has(mediaId!) : false;
                      const tileKey = mediaId || `pending-img-${productId}-${img.role}-${img.altIndex ?? 0}-${idx}`;
                      return (
                        <Box key={`imgq-${tileKey}`} flexShrink={0} w="120px">
                          <Tooltip
                            label={
                              selectable
                                ? (selected ? 'Click to remove from image-ad queue' : 'Click to queue for image ads')
                                : 'image still processing'
                            }
                            hasArrow
                            openDelay={300}
                          >
                            <Box
                              as={selectable ? 'button' : 'div'}
                              onClick={selectable ? () => onToggleImageQueue(mediaId!) : undefined}
                              position="relative"
                              w="120px" h="120px"
                              borderRadius="md"
                              overflow="hidden"
                              bg="gray.100"
                              borderWidth="2px"
                              borderColor={selected ? 'rsViolet.400' : 'brand.border'}
                              cursor={selectable ? 'pointer' : 'not-allowed'}
                              opacity={selectable ? 1 : 0.55}
                              transition="all 120ms"
                              boxShadow={selected ? '0 0 0 3px rgba(122,53,232,0.18)' : undefined}
                              _hover={selectable ? { borderColor: selected ? 'rsViolet.400' : 'gray.300' } : undefined}
                            >
                              <img
                                src={img.imageUrl}
                                alt={`${title} ${img.role}${img.altIndex ? ` ${img.altIndex}` : ''}`}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                loading="lazy"
                              />
                              <Badge
                                position="absolute" top="4px" left="4px"
                                fontSize="9px"
                                colorScheme={img.role === 'hero' ? 'green' : 'gray'}
                                variant="solid"
                              >
                                {img.role === 'hero' ? 'PRIMARY' : `ALT ${img.altIndex ?? ''}`}
                              </Badge>
                              {selected && (
                                <Box
                                  position="absolute" top="4px" right="4px"
                                  bg="rsViolet.500" color="white"
                                  w="20px" h="20px" borderRadius="full"
                                  display="flex" alignItems="center" justifyContent="center"
                                  fontSize="11px" fontWeight="800"
                                >
                                  ✓
                                </Box>
                              )}
                            </Box>
                          </Tooltip>
                        </Box>
                      );
                    })}
                  </HStack>
                </Box>
              </Box>
            )}
          </Box>
        );
      })}
    </VStack>
  );
}

// Group a related-ribbon row list by matchTier. Renders one ribbon per
// non-empty tier with a tier-specific subtitle. Tier 1/2 are derived
// from the per-pick endpoints; brand_match rides along in its own
// section above (not multiplexed here).
const TIER_LABELS: Record<MatchTier, { title: string; subtitle: string }> = {
  product_match:    { title: 'Product match',  subtitle: 'Specific SKU identified in the matched item.' },
  product_category: { title: 'Category match', subtitle: 'Same category, different SKU — broader pairing.' },
  brand_match:      { title: 'Brand match',    subtitle: 'Brand-fit only; no specific product identified.' }
};

function groupByTier<T extends { matchTier?: MatchTier | null }>(rows: T[]): Array<{ tier: MatchTier; items: T[] }> {
  const order: MatchTier[] = ['product_match', 'product_category', 'brand_match'];
  const buckets = new Map<MatchTier, T[]>();
  for (const r of rows) {
    const t = (r.matchTier as MatchTier) || 'product_category';
    if (!buckets.has(t)) buckets.set(t, []);
    buckets.get(t)!.push(r);
  }
  return order.filter(t => buckets.has(t)).map(t => ({ tier: t, items: buckets.get(t)! }));
}

function RelatedTierBlocks<T>({
  heading, tiers, renderTile, keyFor
}: {
  heading: string;
  tiers: Array<{ tier: MatchTier; items: T[] }>;
  renderTile: (item: T, key: string) => React.ReactNode;
  keyFor: (item: T) => string;
}) {
  return (
    <Box>
      <SectionHeader title={heading} subtitle="Click ✕ on any tile to remove that pairing from this run." tone="muted" />
      <VStack align="stretch" spacing={4}>
        {tiers.map(({ tier, items }) => (
          <Box key={tier}>
            <HStack mb={1.5}>
              <Badge fontSize="9px" colorScheme={tier === 'product_match' ? 'green' : tier === 'product_category' ? 'purple' : 'blue'}>
                {TIER_LABELS[tier].title}
              </Badge>
              <Text fontSize="11px" color="brand.muted">{TIER_LABELS[tier].subtitle}</Text>
              <Text fontSize="11px" color="brand.muted" fontWeight="700">· {items.length}</Text>
            </HStack>
            <Box overflowX="auto" overflowY="hidden" pb={2}>
              <HStack spacing={3} align="stretch" minW="min-content">
                {items.map(it => (
                  <Box key={keyFor(it)} flexShrink={0}>{renderTile(it, keyFor(it))}</Box>
                ))}
              </HStack>
            </Box>
          </Box>
        ))}
      </VStack>
    </Box>
  );
}

// Wraps any tile with an exclusion overlay (X) + a hover tooltip
// surfacing the match reason / score / provider winner. When excluded,
// dims the tile and changes the X to a "↺" restore action.
function ExcludeWrapper({
  excluded, onExclude, reason, score, winner, source, stats, children
}: {
  excluded: boolean;
  onExclude: () => void;
  reason: string | null | undefined;
  score: number | null | undefined;
  winner: string | null | undefined;
  source: string | null | undefined;
  stats?: { likes: number | null; comments: number | null; saves: number | null };
  children: React.ReactNode;
}) {
  const tipLines: string[] = [];
  if (reason)  tipLines.push(reason);
  if (score != null) tipLines.push(`score: ${(score * 100).toFixed(0)}%`);
  if (winner) tipLines.push(`via: ${winner}${source ? ` @ ${source}` : ''}`);
  if (stats && (stats.likes != null || stats.comments != null || stats.saves != null)) {
    const parts = [];
    if (stats.likes    != null) parts.push(`${stats.likes} likes`);
    if (stats.comments != null) parts.push(`${stats.comments} comments`);
    if (stats.saves    != null) parts.push(`${stats.saves} saves`);
    tipLines.push(parts.join(' · '));
  }
  const tip = tipLines.join('\n') || 'No match evidence stored.';
  return (
    <Tooltip label={tip} hasArrow placement="top" whiteSpace="pre-line" openDelay={200}>
      <Box position="relative" opacity={excluded ? 0.4 : 1} transition="opacity 120ms">
        {children}
        <Box
          as="button"
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); onExclude(); }}
          position="absolute"
          top={1.5}
          left={1.5}
          w="22px" h="22px"
          borderRadius="full"
          bg={excluded ? 'gray.700' : 'red.500'}
          color="white"
          fontSize="11px"
          fontWeight="800"
          display="flex" alignItems="center" justifyContent="center"
          boxShadow="md"
          _hover={{ transform: 'scale(1.1)' }}
          aria-label={excluded ? 'Restore pairing' : 'Exclude pairing from this run'}
        >
          {excluded ? '↺' : '✕'}
        </Box>
      </Box>
    </Tooltip>
  );
}

function SelectionChip({
  tone, kindLabel, label, thumbSrc, onRemove
}: {
  tone: 'purple' | 'blue';
  kindLabel: string;
  label: string;
  thumbSrc: string | null;
  onRemove: () => void;
}) {
  const bg = tone === 'purple' ? 'rsViolet.50' : 'blue.50';
  const fg = tone === 'purple' ? 'rsViolet.700' : 'blue.700';
  const border = tone === 'purple' ? 'rsViolet.200' : 'blue.200';
  return (
    <HStack
      bg={bg}
      borderWidth="1px"
      borderColor={border}
      borderRadius="md"
      pl={thumbSrc ? 0 : 2}
      pr={2}
      py={thumbSrc ? 0 : 1}
      spacing={2}
      cursor="pointer"
      onClick={onRemove}
      role="button"
      _hover={{ borderColor: tone === 'purple' ? 'rsViolet.400' : 'blue.400' }}
    >
      {thumbSrc && (
        <Image
          src={thumbSrc}
          alt={label}
          w="28px" h="28px"
          objectFit="cover"
          borderLeftRadius="md"
          flexShrink={0}
        />
      )}
      <Text fontSize="xs" color={fg} fontWeight="700" noOfLines={1} maxW="180px">
        {kindLabel} · {label}
      </Text>
      <Text fontSize="xs" color={fg} fontWeight="800" pl={1}>✕</Text>
    </HStack>
  );
}

// Promotional context banner — appears at the top of Step 2 when the
// active campaign is promotional. Keeps the operator oriented while
// picking products/media: shows offer label, promo code, prize (for
// raffle), entries rate, and a countdown when the offer/draw ends
// within the next 14 days.
type PromoContextBannerProps = {
  ctx: {
    discountType:  string | null;
    discountValue: number | null;
    discountCode:  string | null;
    rafflePrize:   string | null;
    raffleEntriesPerDollar: number | null;
    raffleDrawDate: string | null;
    endsAt:        string | null;
  };
};
function PromoContextBanner({ ctx }: PromoContextBannerProps) {
  const isRaffle = ctx.discountType === 'raffle';
  const offerLabel = isRaffle ? 'Raffle / Sweepstakes'
    : ctx.discountType === 'percent'       ? `${ctx.discountValue ?? '—'}% off`
    : ctx.discountType === 'amount'        ? `$${ctx.discountValue ?? '—'} off`
    : ctx.discountType === 'bogo'          ? 'Buy one, get one'
    : ctx.discountType === 'bundle'        ? 'Bundle savings'
    : ctx.discountType === 'gift'          ? 'Free gift'
    : ctx.discountType === 'free_shipping' ? 'Free shipping'
    : 'Promotional';

  const drawOrEnd = (isRaffle ? ctx.raffleDrawDate : null) || ctx.endsAt || null;
  const daysLeft  = drawOrEnd
    ? Math.ceil((new Date(drawOrEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;
  const countdownLabel = isRaffle ? 'Drawing' : 'Ends';
  const urgency = daysLeft != null && Number.isFinite(daysLeft) && daysLeft > 0 && daysLeft <= 14;

  return (
    <Box
      bg="orange.50"
      borderWidth="1px"
      borderColor="orange.200"
      borderRadius="md"
      px={4} py={3}
    >
      <HStack spacing={3} wrap="wrap">
        <Badge colorScheme="orange" variant="solid" fontSize="10px">{offerLabel}</Badge>
        {isRaffle && ctx.rafflePrize && (
          <Text fontSize="sm" color="brand.ink" fontWeight="700">Win: {ctx.rafflePrize}</Text>
        )}
        {isRaffle && ctx.raffleEntriesPerDollar != null && (
          <Text fontSize="sm" color="brand.muted">
            {ctx.raffleEntriesPerDollar} entr{ctx.raffleEntriesPerDollar === 1 ? 'y' : 'ies'} / $1
          </Text>
        )}
        {ctx.discountCode && (
          <HStack spacing={1}>
            <Text fontSize="11px" color="brand.muted">Code:</Text>
            <Text fontSize="11px" fontWeight="700" color="brand.ink" fontFamily="mono">
              {ctx.discountCode}
            </Text>
          </HStack>
        )}
        {urgency && daysLeft != null && (
          <Badge colorScheme="red" variant="subtle" fontSize="10px">
            {countdownLabel} in {daysLeft} day{daysLeft === 1 ? '' : 's'}
          </Badge>
        )}
        {drawOrEnd && !urgency && (
          <Text fontSize="11px" color="brand.muted">
            {countdownLabel}: {new Date(drawOrEnd).toISOString().slice(0, 10)}
          </Text>
        )}
      </HStack>
      <Text fontSize="11px" color="brand.muted" mt={1.5}>
        {isRaffle
          ? 'Headlines anchor on the prize; product becomes the entry mechanic (per-purchase entry counter rides on the product card).'
          : 'Headlines surface the offer. Pick products that fit the offer narrative.'}
      </Text>
    </Box>
  );
}

// ── Product-kind view ───────────────────────────────────────────────
// "Media" ribbon shows only product hero tiles (one per product). After
// any product is picked, a single "Media Related To Your Selection"
// section surfaces every related media — alt catalog images + matched
// UGC posts (dedup'd by mediaId). Each related tile carries a metadata
// strip beneath: "IG catalog · {title} · {updatedAt}" for catalog alts,
// "IG Post · {handle} · {postedAt}" for UGC. Tier expand buttons widen
// what's pulled into the related list.

type ProductRelatedTile =
  | { kind: 'ugc'; media: MediaRow };

// ── Prepare catalog images ──────────────────────────────────────────
//
// 2026-08-19 — the picker fix for "826 of 831 products unpickable" on a
// freshly ingested brand (Pelagic Gear QA). A product card stays
// selectable even with `pickerBlockReason: 'materializing'` (clicking it
// already triggers the pre-existing per-product on-demand backfill), but
// clicking through hundreds of cards one at a time to prepare a whole
// catalog is not a real workflow. This banner gives the operator a single
// button that kicks off the bounded background sweep
// (services/catalogMaterializeDrainService.js on the backend, via
// POST /api/catalog/materialize) and shows real progress instead of
// silence — reuses the EXISTING generic OperationRun poll surface
// (GET /api/progress/:runId), no bespoke progress transport.
type MaterializeRun = {
  id:         string;
  status:     'running' | 'succeeded' | 'failed' | 'cancelled' | 'cancelling';
  itemsDone:  number;
  itemsTotal: number | null;
  pct:        number | null;
};

const TERMINAL_RUN_STATUSES = new Set(['succeeded', 'failed', 'cancelled']);

function PrepareCatalogBanner({ brandId, preparingCount, onPrepared }: {
  brandId: string;
  // Count within the CURRENTLY LOADED page of products — a lower bound
  // on the brand total, used only to decide whether to show the "N
  // products here still need preparing" prompt before the operator has
  // clicked anything. The authoritative total comes back from the POST.
  preparingCount: number;
  onPrepared: () => void;
}) {
  const [run, setRun]         = useState<MaterializeRun | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [candidateInfo, setCandidateInfo] = useState<{ candidates: number; excludedUnusable: number } | null>(null);
  const wasRunningRef = useRef(false);

  const start = useCallback(async () => {
    setStarting(true);
    setError(null);
    try {
      const res = await apiJson<{
        run?: { id: string } | null;
        candidates?: number;
        excludedUnusable?: number;
        error?: string;
      }>('/api/catalog/materialize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brandId }),
      });
      if (typeof res.candidates === 'number') {
        setCandidateInfo({ candidates: res.candidates, excludedUnusable: res.excludedUnusable || 0 });
      }
      if (res.run?.id) {
        setRun({ id: res.run.id, status: 'running', itemsDone: 0, itemsTotal: res.candidates ?? null, pct: null });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start — try again');
    } finally {
      setStarting(false);
    }
  }, [brandId]);

  // Poll the generic progress surface while a run is in flight. 2s matches
  // every other poller in the app (routes/progress.js header comment).
  //
  // The raw OperationRun doc comes back keyed on Mongo's `_id`
  // (routes/progress.js reads via `.lean()`, which strips the `id`
  // virtual) — NOT `id`, which is only what the POST /materialize
  // response uses (progressService's handle.id). Normalize on the way
  // in, or the second poll tick reads `run.id` as undefined and starts
  // hitting `/api/progress/undefined` forever.
  useEffect(() => {
    if (!run || TERMINAL_RUN_STATUSES.has(run.status)) return;
    let cancelled = false;
    const t = window.setInterval(async () => {
      try {
        const res = await apiJson<{ run: MaterializeRun & { _id?: string } }>(`/api/progress/${run.id}`);
        if (!cancelled && res.run) {
          setRun({ ...res.run, id: res.run.id || res.run._id || run.id });
        }
      } catch { /* transient — next tick retries */ }
    }, 2000);
    return () => { cancelled = true; window.clearInterval(t); };
  }, [run?.id, run?.status]);

  // Refetch the product list exactly once, the moment the run finishes —
  // not on every poll tick, and not while still running (the picker would
  // otherwise re-render mid-sweep with a partially-stale page).
  useEffect(() => {
    if (!run) return;
    const isRunning = run.status === 'running' || run.status === 'cancelling';
    if (!isRunning && wasRunningRef.current) onPrepared();
    wasRunningRef.current = isRunning;
  }, [run, onPrepared]);

  const isActive = !!run && (run.status === 'running' || run.status === 'cancelling');
  // Nothing worth showing: no run ever started here, and nothing on the
  // current page is waiting to be prepared.
  if (!run && preparingCount === 0) return null;

  return (
    <Box borderWidth="1px" borderColor="brand.border" borderRadius="md" p={3} bg="rsViolet.50">
      <HStack justify="space-between" align="center" spacing={3}>
        <Box flex="1">
          {isActive ? (
            <>
              <Text fontSize="xs" fontWeight="700" color="brand.ink">
                Preparing catalog images — {run.itemsDone}
                {run.itemsTotal != null ? ` of ${run.itemsTotal}` : ''}
              </Text>
              <Progress
                value={run.pct != null ? Math.round(run.pct * 100) : undefined}
                isIndeterminate={run.pct == null}
                size="xs" colorScheme="purple" mt={1.5} borderRadius="md"
              />
            </>
          ) : run && run.status === 'succeeded' ? (
            <Text fontSize="xs" fontWeight="700" color="green.600">
              ✓ Catalog images prepared — {run.itemsDone} ready
              {candidateInfo?.excludedUnusable ? ` (${candidateInfo.excludedUnusable} had no usable photo and were skipped)` : ''}
            </Text>
          ) : run && (run.status === 'failed' || run.status === 'cancelled') ? (
            <Text fontSize="xs" fontWeight="700" color="orange.600">
              Catalog preparation {run.status} after {run.itemsDone} — you can try again.
            </Text>
          ) : (
            <Text fontSize="xs" color="brand.ink">
              {preparingCount} shown here {preparingCount === 1 ? 'is' : 'are'} still being prepared for ad generation.
              Selecting one starts preparing it, or prepare the whole catalog at once.
            </Text>
          )}
          {error && <Text fontSize="xs" color="red.500" mt={1}>{error}</Text>}
        </Box>
        {!isActive && (
          <Button size="xs" colorScheme="purple" isLoading={starting} onClick={start}>
            Prepare catalog images
          </Button>
        )}
      </HStack>
    </Box>
  );
}

type ProductKindViewProps = {
  products:               ProductRow[];
  productSet:             Set<string>;
  mediaSet:               Set<string>;
  excludedSet:            Set<string>;
  toggleProduct:          (id: string) => void;
  toggleMedia:            (id: string) => void;
  toggleExclude:          (productId: string | null, mediaId: string) => void;
  catalogImagery:         CatalogImageRow[];
  productMatchedRelated:  MediaRow[];
  categoryMatchedRelated: MediaRow[];
  brandMatches:           MediaRow[];
  loadingP:               boolean;
  // Search term currently applied to `products` (server-side filtered)
  // and the last known unfiltered catalog size — together let the empty
  // ribbon state distinguish "search matched nothing" from "catalog is
  // actually empty" (see productRibbonEmptyHint above).
  productQuery:           string;
  catalogSize:            number | null;
  includeCategoryMatched: boolean;
  includeBrandMatched:    boolean;
  onTierToggle:           (field: 'includeCategoryMatched' | 'includeBrandMatched', next: boolean) => void;
  anyProductPicked:       boolean;
  adEligibleFilteredCount: number;
  includeFilteredPromo:   boolean;
  onToggleFilteredPromo:  () => void;
  seedUsage:              Map<string, SeedUsage>;
  // Video-seed picking on related media. Gated the same way the per-product
  // rail is: seedMediaIds is only POSTed when video is in adKinds.
  showVideoSeeds:         boolean;
  onToggleSeed:           (productId: string, mediaId: string) => void;
  seedBadgeFor:           (productId: string, mediaId: string) => number;
};

function ProductKindView(props: ProductKindViewProps) {
  const {
    products, productSet, mediaSet,
    toggleProduct, toggleMedia,
    catalogImagery, productMatchedRelated, categoryMatchedRelated, brandMatches,
    loadingP, productQuery, catalogSize, includeCategoryMatched, includeBrandMatched, onTierToggle, anyProductPicked,
    adEligibleFilteredCount, includeFilteredPromo, onToggleFilteredPromo,
    seedUsage, showVideoSeeds, onToggleSeed, seedBadgeFor
  } = props;
  // excludedSet / toggleExclude still on props for API symmetry with
  // BrandUnifiedView; product-kind related ribbon is UGC-only now so
  // pairing exclusions aren't rendered here.
  void props.excludedSet;
  void props.toggleExclude;

  // Related UGC only — catalog hero/alts live in the per-product
  // Video seeds / Image-ad queue sections (not interleaved here).
  // Optional category/brand tiers ride behind the expand toggles.
  void catalogImagery;
  const relatedTiles: ProductRelatedTile[] = [];
  const seenMediaIds = new Set<string>();

  for (const m of productMatchedRelated) {
    if (seenMediaIds.has(m.id)) continue;
    seenMediaIds.add(m.id);
    relatedTiles.push({ kind: 'ugc', media: m });
  }
  if (includeCategoryMatched) {
    for (const m of categoryMatchedRelated) {
      if (seenMediaIds.has(m.id)) continue;
      seenMediaIds.add(m.id);
      relatedTiles.push({ kind: 'ugc', media: m });
    }
  }
  if (includeBrandMatched) {
    for (const m of brandMatches) {
      if (seenMediaIds.has(m.id)) continue;
      seenMediaIds.add(m.id);
      relatedTiles.push({ kind: 'ugc', media: m });
    }
  }

  return (
    <>
      {/* Primary ribbon — no inner header (StepShell carries it). */}
      <RibbonPicker
        items={products}
        getId={p => p.id}
        selectedIds={productSet}
        onToggle={toggleProduct}
        loading={loadingP}
        emptyHint={productRibbonEmptyHint(productQuery, catalogSize)}
        render={(p, selected, onClick) => (
          <ProductTile product={p} selected={selected} onClick={onClick} usage={seedUsage.get(p.id)} />
        )}
      />

      {anyProductPicked && (
        <>
          <SectionHeader
            title={`Related media (${relatedTiles.length})`}
            subtitle="Social posts that go with what you picked. Catalog images are in the per-product sections below. Tap a post to queue it for image ads; use the ▶ badge to add it as a numbered video seed."
            tone="muted"
          />
          {relatedTiles.length === 0 ? (
            <Box h="100px" bg="gray.50" borderRadius="md" display="flex" alignItems="center" justifyContent="center" px={4}>
              <Text fontSize="xs" color="brand.muted" textAlign="center">
                {adEligibleFilteredCount > 0 && !includeFilteredPromo
                  ? `${adEligibleFilteredCount} matched post${adEligibleFilteredCount === 1 ? '' : 's'} hidden — classifier flagged ${adEligibleFilteredCount === 1 ? 'it' : 'them'} as promotional or announcement. Use the toggle below to show ${adEligibleFilteredCount === 1 ? 'it' : 'them'} anyway.`
                  : 'Nothing related yet. Try the buttons below to widen the pool.'}
              </Text>
            </Box>
          ) : (
            <Box
              overflowX="auto"
              overflowY="hidden"
              pb={2}
              sx={{
                '&::-webkit-scrollbar':       { height: '8px' },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.18)', borderRadius: '4px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' }
              }}
            >
              <HStack spacing={3} align="stretch" minW="min-content">
                {relatedTiles.map((t, idx) => {
                  if (t.kind !== 'ugc') return <Box key={`pr-${idx}`} flexShrink={0} />;
                  const m = t.media;
                  // Seedable only when detect produced a real SKU match AND the
                  // post is attributed to a product. This mirrors the backend
                  // exactly (resolveSeedProductId requires
                  // matchedProducts[].outcome === 'product_match'), so a pick
                  // the operator can make is a pick that will actually be used —
                  // no silent drops at generate time.
                  const seedable  = showVideoSeeds && m.matchTier === 'product_match' && !!m.seedProductId;
                  const seedBadge = seedable ? seedBadgeFor(m.seedProductId!, m.id) : 0;
                  return (
                    <Box key={`pr-${m.id}`} flexShrink={0} position="relative">
                      <ProductRelatedUgcTile
                        media={m}
                        selected={mediaSet.has(m.id)}
                        onToggle={() => toggleMedia(m.id)}
                        usage={seedUsage.get(m.id)}
                      />
                      {showVideoSeeds && (
                        <Tooltip
                          hasArrow
                          openDelay={300}
                          label={
                            !seedable
                              ? 'Can’t seed a video — this post isn’t matched to one of the selected products'
                              : seedBadge
                                ? `Video seed #${seedBadge} — click to remove`
                                : 'Use as a video seed'
                          }
                        >
                          <Box
                            as={seedable ? 'button' : 'div'}
                            onClick={seedable ? (e: MouseEvent) => { e.stopPropagation(); onToggleSeed(m.seedProductId!, m.id); } : undefined}
                            position="absolute"
                            top="4px" left="4px"
                            minW="22px" h="22px" px={seedBadge ? '5px' : 0}
                            borderRadius="full"
                            display="flex" alignItems="center" justifyContent="center"
                            fontSize="11px" fontWeight="800"
                            bg={seedBadge ? 'rsViolet.500' : 'rgba(0,0,0,0.55)'}
                            color="white"
                            opacity={seedable ? 1 : 0.35}
                            cursor={seedable ? 'pointer' : 'not-allowed'}
                            borderWidth={seedBadge ? '2px' : 0}
                            borderColor="white"
                            _hover={seedable ? { bg: seedBadge ? 'rsViolet.600' : 'rgba(0,0,0,0.75)' } : undefined}
                          >
                            {seedBadge || '▶'}
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                  );
                })}
              </HStack>
            </Box>
          )}

          <HStack spacing={3} pt={1} wrap="wrap">
            <Button
              size="xs"
              variant={includeCategoryMatched ? 'softBrand' : 'outline'}
              onClick={() => onTierToggle('includeCategoryMatched', !includeCategoryMatched)}
              isDisabled={categoryMatchedRelated.length === 0 && !includeCategoryMatched}
            >
              {includeCategoryMatched ? '✓ ' : '+ '}
              Add same-category posts ({categoryMatchedRelated.length})
            </Button>
            <Button
              size="xs"
              variant={includeBrandMatched ? 'softBrand' : 'outline'}
              onClick={() => onTierToggle('includeBrandMatched', !includeBrandMatched)}
              isDisabled={brandMatches.length === 0 && !includeBrandMatched}
            >
              {includeBrandMatched ? '✓ ' : '+ '}
              Add brand-only posts ({brandMatches.length})
            </Button>
            {/* Show only when the classifier blocked at least one match
                — irrelevant copy otherwise. Toggle re-fetches with
                adEligible=0 so the previously-hidden posts come through. */}
            {(adEligibleFilteredCount > 0 || includeFilteredPromo) && (
              <Button
                size="xs"
                variant={includeFilteredPromo ? 'softBrand' : 'outline'}
                onClick={onToggleFilteredPromo}
                title="Include posts the classifier flagged promotional or announcement"
              >
                {includeFilteredPromo ? '✓ ' : '+ '}
                Show promotional posts{adEligibleFilteredCount > 0 ? ` (${adEligibleFilteredCount})` : ''}
              </Button>
            )}
          </HStack>
        </>
      )}
    </>
  );
}

// UGC tile in the product-kind related ribbon. Wraps MediaTile and
// stacks a dense info block beneath: platform + post type, ad readiness,
// classification + content nature, engagement, source + relation, date.
function ProductRelatedUgcTile({
  media, selected, onToggle, usage
}: {
  media:    MediaRow;
  selected: boolean;
  onToggle: () => void;
  usage?:   SeedUsage;
}) {
  const platform = platformLabel(media.source);
  const postType = (media.postType || media.fileType || '').toLowerCase();
  const postTypeLabel = formatPostType(postType, media.fileType);
  const relation     = matchTierLabel(media.matchTier);
  const ad           = adReadinessBucket(media.adReadiness);
  const eng          = formatEngagement(media);
  const handle       = media.creatorHandle ? (media.creatorHandle.startsWith('@') ? media.creatorHandle : `@${media.creatorHandle}`) : null;
  const date         = formatShortDate(media.postedAt);
  return (
    <Box w="148px">
      <Box position="relative">
        <MediaTile media={media} selected={selected} onClick={onToggle} usage={usage} />
        {/* Platform + post type chip — top left */}
        <Box
          position="absolute" top="4px" left="4px"
          bg="blackAlpha.700" color="white"
          px={1.5} py={0.5} borderRadius="sm"
          fontSize="9px" fontWeight="700" letterSpacing="0.04em"
          pointerEvents="none"
        >
          {platform}{postTypeLabel ? ` · ${postTypeLabel}` : ''}
        </Box>
        {/* Ad readiness chip — top right */}
        {ad && (
          <Box
            position="absolute" top="4px" right="4px"
            bg={ad.bg} color="white"
            px={1.5} py={0.5} borderRadius="sm"
            fontSize="9px" fontWeight="800" letterSpacing="0.04em"
            pointerEvents="none"
            title={`Ad readiness: ${ad.label}`}
          >
            {ad.short}
          </Box>
        )}
      </Box>
      <Box mt={1.5} fontSize="10px" lineHeight="1.35" color="brand.muted">
        {/* Classification row — content nature + shot type */}
        {(media.contentNature || media.shotType) && (
          <Text noOfLines={1} color="brand.ink" fontWeight="600">
            {[media.contentNature, media.shotType].filter(Boolean).map(titleCase).join(' · ')}
          </Text>
        )}
        {/* Engagement row */}
        {eng && (
          <Text noOfLines={1} mt={0.5}>{eng}</Text>
        )}
        {/* Relation row — match tier + handle */}
        <Text noOfLines={1} mt={0.5}>
          {[relation, handle].filter(Boolean).join(' · ')}
        </Text>
        {/* Date */}
        {date && <Text noOfLines={1} mt={0.5} color="brand.muted">{date}</Text>}
      </Box>
    </Box>
  );
}

// ── Rich-tile helpers ────────────────────────────────────────────────
// Compact formatters used by the redesigned related-media tiles. Each
// returns either a short string or a small object the tile renders
// inline. Null returns drop the chip silently when data isn't present.

function platformLabel(src: string | null | undefined): string {
  if (!src) return 'Post';
  const s = String(src).toLowerCase();
  if (s === 'instagram') return 'IG';
  if (s === 'tiktok')    return 'TikTok';
  if (s === 'manual-upload') return 'Upload';
  if (s === 'catalog-product') return 'Catalog';
  return titleCase(s);
}

function formatPostType(postType: string, fileType?: 'image' | 'video'): string {
  const pt = (postType || '').toLowerCase();
  if (pt === 'reel')     return 'Reel';
  if (pt === 'carousel') return 'Carousel';
  if (pt === 'story')    return 'Story';
  if (pt === 'video' || fileType === 'video') return 'Video';
  if (pt === 'image' || fileType === 'image') return 'Photo';
  return '';
}

function matchTierLabel(tier: MatchTier | null | undefined): string {
  switch (tier) {
    case 'product_match':    return 'Tagged this product';
    case 'product_category': return 'Same category';
    case 'brand_match':      return 'Brand-only post';
    default:                 return '';
  }
}

// 0–1 score bucketed into Strong / OK / Weak with corresponding colors
// so the chip is glanceable. Null when score is missing.
function adReadinessBucket(score: number | null | undefined): { short: string; label: string; bg: string } | null {
  if (typeof score !== 'number') return null;
  const pct = Math.round(score * 100);
  if (score >= 0.75) return { short: `★ ${pct}`, label: `Strong (${pct}/100)`, bg: 'rgba(16,185,129,0.85)' };  // emerald
  if (score >= 0.50) return { short: `${pct}`,    label: `OK (${pct}/100)`,     bg: 'rgba(245,158,11,0.85)' };  // amber
  return                        { short: `${pct}`, label: `Weak (${pct}/100)`,  bg: 'rgba(239,68,68,0.85)' };   // red
}

// Engagement string — prefer the engagement rate when available
// (formatted as percent), else fall back to "1.2K ♥ · 84 💬" totals.
// Compact numbers via Intl.NumberFormat 'compact' style.
function formatEngagement(m: MediaRow): string {
  if (typeof m.engagement === 'number' && m.engagement > 0) {
    return `${(m.engagement * 100).toFixed(1)}% eng`;
  }
  const bits: string[] = [];
  const fmt = (n: number) => new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(n);
  if (typeof m.likes    === 'number' && m.likes    > 0) bits.push(`${fmt(m.likes)} ♥`);
  if (typeof m.comments === 'number' && m.comments > 0) bits.push(`${fmt(m.comments)} 💬`);
  if (typeof m.saves    === 'number' && m.saves    > 0) bits.push(`${fmt(m.saves)} ⤴`);
  return bits.join(' · ');
}

function titleCase(s: string | null | undefined): string {
  if (!s) return '';
  return String(s).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.valueOf())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function SectionHeader({ title, subtitle, tone }: { title: string; subtitle?: string; tone?: 'muted' }) {
  return (
    <HStack justify="space-between" align="baseline" mb={2} mt={tone === 'muted' ? 2 : 0}>
      <Box>
        <Text
          fontSize="xs" fontWeight="800"
          color={tone === 'muted' ? 'brand.muted' : 'brand.ink'}
          textTransform="uppercase" letterSpacing="0.06em"
        >
          {title}
        </Text>
        {subtitle && <Text fontSize="11px" color="brand.muted" mt={0.5}>{subtitle}</Text>}
      </Box>
    </HStack>
  );
}

// ── Brand-kind unified view ──────────────────────────────────────────
// Collapses the legacy multi-section brand-campaign picker into two
// ribbons: a Media ribbon mixing product hero tiles + UGC tiles, and
// a Recommended ribbon below that responds to selection. Catalog alts
// surface as informational image-only tiles inside the recommended
// ribbon (no toggle — they fan out automatically when their product
// is picked). Tier expand buttons widen what's pulled into recommended.
type UnifiedTile =
  | { kind: 'product'; product: ProductRow }
  | { kind: 'media';   media:   MediaRow }
  | { kind: 'catalog_alt'; img: CatalogImageRow };

type BrandUnifiedViewProps = {
  products:               ProductRow[];
  media:                  MediaRow[];
  productSet:             Set<string>;
  mediaSet:               Set<string>;
  excludedSet:            Set<string>;
  toggleProduct:          (id: string) => void;
  toggleMedia:            (id: string) => void;
  toggleExclude:          (productId: string | null, mediaId: string) => void;
  relatedProducts:        ProductRow[];
  productMatchedRelated:  MediaRow[];
  categoryMatchedRelated: MediaRow[];
  brandMatches:           MediaRow[];
  catalogImagery:         CatalogImageRow[];
  loadingP:               boolean;
  loadingM:               boolean;
  includeCategoryMatched: boolean;
  includeBrandMatched:    boolean;
  onTierToggle:           (field: 'includeCategoryMatched' | 'includeBrandMatched', next: boolean) => void;
  anyPicks:               boolean;
  // Set of Media IDs eligible for brand campaigns (shotType ∈
  // {lifestyle, on_model, unknown} or unset). null while the fetch
  // is in flight — treat as "include everything" to avoid flashing
  // an empty ribbon on mount. Applied to alts + UGC; heroes pass
  // through unfiltered.
  lifestyleEligibleIds:   Set<string> | null;
  seedUsage:              Map<string, SeedUsage>;
};

function BrandUnifiedView(props: BrandUnifiedViewProps) {
  const {
    products, media, productSet, mediaSet, excludedSet,
    toggleProduct, toggleMedia, toggleExclude,
    relatedProducts, productMatchedRelated, categoryMatchedRelated, brandMatches, catalogImagery,
    loadingP, loadingM, includeCategoryMatched, includeBrandMatched, onTierToggle, anyPicks,
    lifestyleEligibleIds, seedUsage
  } = props;

  // Lifestyle filter helpers. While the eligible-ID set is loading
  // (null) we let everything through to avoid an empty-ribbon flash;
  // once loaded, alts + UGC must be in the set, but legacy alts with
  // no Media wrapper (imageMediaId === null) get included as well so
  // pre-classifier catalog rows aren't silently hidden.
  const altLifestyleOk = (altMediaId: string | null) => {
    if (!lifestyleEligibleIds) return true;
    if (!altMediaId) return true;
    return lifestyleEligibleIds.has(altMediaId);
  };
  const mediaLifestyleOk = (mediaId: string) => {
    if (!lifestyleEligibleIds) return true;
    return lifestyleEligibleIds.has(mediaId);
  };

  // Build the unified pool of tiles (UGC + brand-only posts + catalog
  // alts + product heroes), score each, then sort the whole thing
  // left → right. Operator sees the best ad-fit tiles first regardless
  // of source.
  //
  // Score weights (see scoreTile below):
  //   media UGC:
  //     +30  lifestyle / on_model shotType
  //     +25  brand_match (brand-only posts that match the brand but
  //          no specific product — high signal for brand campaigns)
  //     +20  evergreen contentNature
  //     ×100 adReadiness (0–1)
  //     ×50  engagement rate (0–1; fallback to clamped likes+comments)
  //   catalog_alt:
  //     +25  imageMediaId in lifestyleEligibleIds (lifestyle alt)
  //     +15  baseline (clean studio shot, usually safe for ads)
  //   product hero:
  //     +10  baseline (constant brand inventory)
  //
  // brand_match posts get folded into the same pool so they sort
  // alongside everything else instead of living in a separate "rec-
  // ommended" rail.
  type UnifiedScored = { tile: UnifiedTile; score: number };
  const scored: UnifiedScored[] = [];
  // UGC media + brand-only posts. Dedup by id since brand_match can
  // overlap with the main media list.
  const seenMediaIds = new Set<string>();
  for (const m of media) {
    if (!mediaLifestyleOk(m.id)) continue;
    if (seenMediaIds.has(m.id)) continue;
    seenMediaIds.add(m.id);
    scored.push({ tile: { kind: 'media', media: m }, score: scoreMedia(m) });
  }
  for (const m of brandMatches) {
    if (!mediaLifestyleOk(m.id)) continue;
    if (seenMediaIds.has(m.id)) continue;
    seenMediaIds.add(m.id);
    scored.push({ tile: { kind: 'media', media: m }, score: scoreMedia(m) });
  }
  // Product heroes only in the brand ribbon. Catalog alts used to mix
  // into this flat row and toggle mediaIds with a bare ✓ — that was the
  // wrong picker (no order, no per-product grouping). Alts now live in
  // the per-product Video seeds / Image-ad queue sections below.
  void altLifestyleOk;
  for (const p of products) {
    scored.push({ tile: { kind: 'product', product: p }, score: 10 });
  }
  scored.sort((a, b) => b.score - a.score);
  const mainTiles: UnifiedTile[] = scored.map(s => s.tile);

  // Subordinate ribbon — operator's queued picks. NO auto-fanout of
  // related/matched media; the queue is whatever the operator has
  // actively selected from the primary ribbon above. Alts are now
  // independently selectable via mediaSet (toggle their imageMediaId)
  // so they queue exactly like UGC tiles.
  void catalogImagery;            // product-kind only — kept on props for symmetry
  void relatedProducts;           // recommendations no longer auto-surface in brand-kind subordinate
  void productMatchedRelated;
  void categoryMatchedRelated;
  void includeCategoryMatched;
  void includeBrandMatched;
  void onTierToggle;
  const queuedTiles = mainTiles.filter(t => {
    if (t.kind === 'product') return productSet.has(t.product.id);
    if (t.kind === 'media')   return mediaSet.has(t.media.id);
    // catalog_alt no longer surfaces in the brand ribbon.
    return false;
  });

  return (
    <>
      {/* Primary ribbon — no inner header (StepShell carries it). */}
      <UnifiedRibbon
        tiles={mainTiles}
        loading={loadingP || loadingM}
        emptyHint="No media yet. Connect Instagram or a catalog to get started."
        productSet={productSet}
        mediaSet={mediaSet}
        excludedSet={excludedSet}
        toggleProduct={toggleProduct}
        toggleMedia={toggleMedia}
        toggleExclude={toggleExclude}
        seedUsage={seedUsage}
      />

      {anyPicks && (
        <>
          <SectionHeader
            title={`Queued for this run (${queuedTiles.length})`}
            subtitle="Just what you picked above. Tap any tile to remove it from the queue."
            tone="muted"
          />
          <UnifiedRibbon
            tiles={queuedTiles}
            loading={false}
            productSet={productSet}
            mediaSet={mediaSet}
            excludedSet={excludedSet}
            toggleProduct={toggleProduct}
            toggleMedia={toggleMedia}
            toggleExclude={toggleExclude}
            seedUsage={seedUsage}
          />
        </>
      )}
    </>
  );
}

// Composite ad-fit score for UGC tiles in the brand-kind primary
// ribbon. Higher = ranks further left. Brand-only matches get an
// explicit boost so they don't sit at the bottom of the engagement
// sort despite being highly relevant to brand campaigns.
function scoreMedia(m: MediaRow): number {
  let score = 0;
  const shot = String(m.shotType || '').toLowerCase();
  if (shot === 'lifestyle' || shot === 'on_model') score += 30;
  if (m.matchTier === 'brand_match') score += 25;
  if (String(m.contentNature || '').toLowerCase() === 'evergreen') score += 20;
  if (typeof m.adReadiness === 'number') score += m.adReadiness * 100;
  if (typeof m.engagement  === 'number' && m.engagement > 0) {
    score += m.engagement * 50;
  } else {
    const totals = (m.likes ?? 0) + (m.comments ?? 0);
    score += Math.min(50, totals / 200);
  }
  return score;
}

type UnifiedRibbonProps = {
  tiles:         UnifiedTile[];
  loading:       boolean;
  emptyHint?:    string;
  productSet:    Set<string>;
  mediaSet:      Set<string>;
  excludedSet:   Set<string>;
  toggleProduct: (id: string) => void;
  toggleMedia:   (id: string) => void;
  toggleExclude: (productId: string | null, mediaId: string) => void;
  seedUsage:     Map<string, SeedUsage>;
};

function UnifiedRibbon(props: UnifiedRibbonProps) {
  const { tiles, loading, emptyHint, productSet, mediaSet, excludedSet, toggleProduct, toggleMedia, toggleExclude, seedUsage } = props;

  if (loading && tiles.length === 0) {
    return (
      <Box h="120px" bg="gray.50" borderRadius="md" display="flex" alignItems="center" justifyContent="center">
        <Text fontSize="xs" color="brand.muted">Loading…</Text>
      </Box>
    );
  }
  if (tiles.length === 0) {
    return (
      <Box h="120px" bg="gray.50" borderRadius="md" display="flex" alignItems="center" justifyContent="center">
        <Text fontSize="xs" color="brand.muted">{emptyHint || 'Nothing here yet.'}</Text>
      </Box>
    );
  }
  return (
    <Box
      overflowX="auto"
      overflowY="hidden"
      pb={2}
      sx={{
        '&::-webkit-scrollbar':       { height: '8px' },
        '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.18)', borderRadius: '4px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' }
      }}
    >
      <HStack spacing={3} align="stretch" minW="min-content">
        {tiles.map((t, idx) => {
          if (t.kind === 'product') {
            const p = t.product;
            const sel = productSet.has(p.id);
            const key = pairingKey(p.id, p.seedMediaId || '');
            const excluded = p.seedMediaId ? excludedSet.has(key) : false;
            return (
              <Box key={`p-${p.id}-${idx}`} flexShrink={0} position="relative" opacity={excluded ? 0.4 : 1}>
                <ProductTile product={p} selected={sel} onClick={() => toggleProduct(p.id)} usage={seedUsage.get(p.id)} />
                {p.seedMediaId && (
                  <Box
                    as="button"
                    onClick={(e: MouseEvent) => { e.stopPropagation(); toggleExclude(p.id, p.seedMediaId || ''); }}
                    position="absolute"
                    top="4px"
                    right="4px"
                    w="18px" h="18px" borderRadius="full"
                    bg="rgba(0,0,0,0.55)" color="white"
                    fontSize="11px" fontWeight="800"
                    display="flex" alignItems="center" justifyContent="center"
                    title={excluded ? 'Re-include this pairing' : 'Exclude this pairing'}
                  >
                    {excluded ? '+' : '×'}
                  </Box>
                )}
              </Box>
            );
          }
          if (t.kind === 'media') {
            const m = t.media;
            const sel = mediaSet.has(m.id);
            return (
              <Box key={`m-${m.id}-${idx}`} flexShrink={0}>
                <ProductRelatedUgcTile
                  media={m}
                  selected={sel}
                  onToggle={() => toggleMedia(m.id)}
                  usage={seedUsage.get(m.id)}
                />
              </Box>
            );
          }
          // catalog_alt — independently selectable. Click toggles the
          // alt's imageMediaId into mediaSet (the same data structure
          // UGC media tiles use), so the operator queues each alt
          // explicitly rather than relying on auto-fanout from a
          // product pick. Alts with no Media doc id (legacy catalog
          // row without the wrapper materialized) render as
          // informational and can't be toggled.
          const img = t.img;
          const altSelectable = !!img.imageMediaId;
          const altSelected   = altSelectable ? mediaSet.has(img.imageMediaId!) : false;
          return (
            <Box key={`alt-${img.productId}-${img.altIndex || 0}-${idx}`} flexShrink={0}>
              <Box
                as={altSelectable ? 'button' : 'div'}
                onClick={altSelectable ? () => toggleMedia(img.imageMediaId!) : undefined}
                position="relative"
                w="120px" h="120px"
                borderRadius="md"
                overflow="hidden"
                bg="gray.100"
                borderWidth="2px"
                borderColor={altSelected ? 'rsViolet.400' : 'brand.border'}
                cursor={altSelectable ? 'pointer' : 'default'}
                transition="all 120ms"
                _hover={altSelectable ? { borderColor: altSelected ? 'rsViolet.400' : 'gray.300' } : undefined}
                boxShadow={altSelected ? '0 0 0 3px rgba(122,53,232,0.18)' : undefined}
                title={altSelected ? 'Click to remove from queue' : 'Click to queue this alt'}
              >
                <img
                  src={img.imageUrl}
                  alt={`${img.productTitle || 'product'} alt ${img.altIndex || ''}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  loading="lazy"
                />
                <Badge
                  position="absolute" top="4px" left="4px"
                  fontSize="9px"
                  colorScheme={altSelected ? 'purple' : 'gray'}
                  variant="solid"
                >
                  ALT {img.altIndex}
                </Badge>
                {altSelected && (
                  <Box
                    position="absolute" top="4px" right="4px"
                    bg="rsViolet.500" color="white"
                    w="20px" h="20px" borderRadius="full"
                    display="flex" alignItems="center" justifyContent="center"
                    fontSize="11px" fontWeight="800"
                  >
                    ✓
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </HStack>
    </Box>
  );
}
